/**
 * Elite Batch 16 Part 1 — Workflow Templates 1448–1467
 * Vendor contract renewal, vendor onboarding, visa requirements, webhook routing,
 * webinar registration, webinar nurture, website monitoring, form-to-DB pipeline,
 * weekly report generator, weekly status email, team digest, win/loss analysis,
 * work request intake, workflow error handler, workplace incident reporting,
 * book summary bot, browser tab saver, calendar prep, daily affirmation, daily gratitude.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 16 PART 1 — TEMPLATES 1448–1467
// =============================================================================

export const ELITE_BATCH16_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1448. VENDOR CONTRACT RENEWAL TRACKER (9 nodes)
  // ===================================================================

  "Vendor Contract Renewal Tracker": {
    summary:
      "Runs daily to fetch active vendor contracts from HubSpot, classifies renewal windows with runCode, AI-generates renewal summaries, looks up responsible stakeholders, then sends 60-day email reminders, 30-day Slack escalations, 7-day critical SMS alerts, and logs renewal status to Sheets.",
    nodes: [
      trigger("Schedule: Daily vendor contract renewal scan", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily at 8 AM to scan all active vendor contracts for upcoming renewal deadlines, checking 60-day, 30-day, and 7-day windows",
        setup:
          "Configure HubSpot API token with access to deals/contracts. Set up Slack webhook for procurement channel. Prepare Google Sheets for renewal tracking log.",
        inputFields: [],
      }),

      // 2. Fetch active contracts from HubSpot
      hubspotGetNode(
        "2",
        "Fetch active vendor contracts from HubSpot",
        280,
        'crm/v3/objects/deals?properties=dealname,closedate,amount,dealstage,hubspot_owner_id,hs_lastmodifieddate&filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"EQ","value":"contractsigned"}]}]&limit=100',
        [
          { name: "results", customName: "active_contracts", type: "string" },
          { name: "total", customName: "total_contracts", type: "number" },
          {
            name: "results[0].properties.dealname",
            customName: "first_contract_name",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "first_renewal_date",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "first_contract_value",
            type: "string",
          },
          {
            name: "results[0].properties.hubspot_owner_id",
            customName: "first_owner_id",
            type: "string",
          },
        ],
      ),

      // 3. Renewal window classifier
      n("3", "runCode", "Classify contracts by renewal urgency window", 520, {
        runCodeDetails: {
          code: `const today = new Date();
const contracts = JSON.parse(variables.active_contracts || '[]');
const critical = []; const urgent = []; const upcoming = [];
contracts.forEach(c => {
  const renewalDate = new Date(c.properties.closedate);
  const daysUntil = Math.ceil((renewalDate - today) / (1000 * 60 * 60 * 24));
  const entry = { name: c.properties.dealname, date: c.properties.closedate, value: c.properties.amount, days: daysUntil, owner: c.properties.hubspot_owner_id };
  if (daysUntil <= 7) critical.push(entry);
  else if (daysUntil <= 30) urgent.push(entry);
  else if (daysUntil <= 60) upcoming.push(entry);
});
output.critical_contracts = JSON.stringify(critical);
output.critical_count = critical.length;
output.urgent_contracts = JSON.stringify(urgent);
output.urgent_count = urgent.length;
output.upcoming_contracts = JSON.stringify(upcoming);
output.upcoming_count = upcoming.length;
output.total_actionable = critical.length + urgent.length + upcoming.length;`,
          outputVariables: [
            { name: "critical_contracts", type: "string" },
            { name: "critical_count", type: "number" },
            { name: "urgent_contracts", type: "string" },
            { name: "urgent_count", type: "number" },
            { name: "upcoming_contracts", type: "string" },
            { name: "upcoming_count", type: "number" },
            { name: "total_actionable", type: "number" },
          ],
        },
      }),

      // 4. AI renewal summary generator
      aiNode(
        "4",
        "AI generate renewal summaries and recommendations",
        760,
        "You are a procurement analyst. Analyze these vendor contracts approaching renewal and generate actionable summaries. For each group (critical 7-day, urgent 30-day, upcoming 60-day), provide a brief assessment, negotiation tips based on contract value, and recommended next steps. Return JSON: {executive_summary, critical_summary, urgent_summary, upcoming_summary, total_value_at_risk, top_recommendation}",
        "Critical contracts (within 7 days): {critical_contracts}\\nUrgent contracts (within 30 days): {urgent_contracts}\\nUpcoming contracts (within 60 days): {upcoming_contracts}\\nTotal actionable: {total_actionable}",
        [
          {
            field: "executive_summary",
            as: "renewal_executive_summary",
            type: "string",
          },
          {
            field: "critical_summary",
            as: "critical_action_items",
            type: "string",
          },
          {
            field: "urgent_summary",
            as: "urgent_action_items",
            type: "string",
          },
          {
            field: "upcoming_summary",
            as: "upcoming_action_items",
            type: "string",
          },
          {
            field: "total_value_at_risk",
            as: "total_value_at_risk",
            type: "string",
          },
          {
            field: "top_recommendation",
            as: "top_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Stakeholder lookup
      n(
        "5",
        "runCode",
        "Resolve stakeholder contacts for each renewal window",
        1000,
        {
          runCodeDetails: {
            code: `const critical = JSON.parse(variables.critical_contracts || '[]');
const urgent = JSON.parse(variables.urgent_contracts || '[]');
const upcoming = JSON.parse(variables.upcoming_contracts || '[]');
const allOwnerIds = [...new Set([...critical, ...urgent, ...upcoming].map(c => c.owner).filter(Boolean))];
output.stakeholder_ids = allOwnerIds.join(',');
output.critical_owners_summary = critical.map(c => c.name + ' (Owner: ' + c.owner + ', Value: $' + c.value + ', Days: ' + c.days + ')').join('; ');
output.urgent_owners_summary = urgent.map(c => c.name + ' (Owner: ' + c.owner + ', Value: $' + c.value + ', Days: ' + c.days + ')').join('; ');
output.upcoming_owners_summary = upcoming.map(c => c.name + ' (Owner: ' + c.owner + ', Value: $' + c.value + ', Days: ' + c.days + ')').join('; ');`,
            outputVariables: [
              { name: "stakeholder_ids", type: "string" },
              { name: "critical_owners_summary", type: "string" },
              { name: "urgent_owners_summary", type: "string" },
              { name: "upcoming_owners_summary", type: "string" },
            ],
          },
        },
      ),

      // 6. 60-day email reminder
      emailNode(
        "6",
        "Send 60-day renewal reminder email",
        1240,
        "{procurement_team_email}",
        "Vendor Contract Renewals: {upcoming_count} contracts due within 60 days",
        "Hi Procurement Team,\\n\\nThe following vendor contracts are approaching renewal within 60 days:\\n\\n{upcoming_owners_summary}\\n\\nSummary: {upcoming_action_items}\\n\\nTotal value at risk: {total_value_at_risk}\\n\\nRecommendation: {top_recommendation}\\n\\nPlease begin renewal discussions with these vendors.\\n\\nBest,\\nContract Renewal Automation",
      ),

      // 7. 30-day Slack escalation
      slackNode(
        "7",
        "Post 30-day urgent renewal escalation to Slack",
        1480,
        ":warning: *Urgent Vendor Renewals - 30 Day Window*\\n>{urgent_count} contracts need immediate attention\\n>\\n>{urgent_owners_summary}\\n>\\n>*Action Items:* {urgent_action_items}\\n>*Total Value at Risk:* {total_value_at_risk}\\n>\\n>Please prioritize renewal negotiations for these vendors.",
      ),

      // 8. 7-day critical SMS alert
      smsNode(
        "8",
        "Send 7-day critical renewal SMS alert",
        1720,
        "{procurement_lead_phone}",
        "CRITICAL: {critical_count} vendor contracts expiring within 7 days. Contracts: {critical_owners_summary}. Immediate action required. Total value at risk: {total_value_at_risk}",
      ),

      // 9. Renewal status logger
      sheetsNode(
        "9",
        "Log renewal scan results to tracking spreadsheet",
        1960,
        "RenewalTracker",
        `[["${new Date().toISOString()}", "{total_contracts}", "{total_actionable}", "{critical_count}", "{urgent_count}", "{upcoming_count}", "{total_value_at_risk}", "{renewal_executive_summary}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1449. VENDOR ONBOARDING (10 nodes)
  // ===================================================================

  "Vendor Onboarding": {
    summary:
      "Receives new vendor webhook, AI-extracts vendor data, AI-validates tax documents, verifies tax ID against IRS API, gates on validation pass/fail, creates vendor profile in HubSpot, syncs to ERP system, sends welcome packet email, notifies procurement team via Slack, and sends compliance document request email.",
    nodes: [
      trigger("Webhook: New vendor registration submitted", "webhook_app", {
        source: "vendor_portal",
        event: "vendor.registered",
        howItStarts:
          "Fires when a new vendor submits registration through the vendor portal, sending the raw registration payload with company details, tax documentation, banking information, and contact data",
        setup:
          "Configure vendor portal to POST registration webhooks to this URL. Set up IRS TIN validation API access. Configure HubSpot for vendor CRM tracking. Prepare ERP integration credentials.",
        inputFields: [],
      }),

      // 2. AI extract vendor data
      aiNode(
        "2",
        "AI extract vendor registration data from webhook",
        280,
        "Extract vendor registration data from this raw webhook payload. Find the company name, legal entity name, tax ID (EIN/TIN), business address, primary contact name, primary contact email, primary contact phone, business type, annual revenue range, products/services offered, and any uploaded document references regardless of field naming format. Return JSON: {company_name, legal_entity, tax_id, business_address, contact_name, contact_email, contact_phone, business_type, revenue_range, services_offered, document_urls}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "company_name", as: "vendor_company", type: "string" },
          { field: "legal_entity", as: "vendor_legal_entity", type: "string" },
          { field: "tax_id", as: "vendor_tax_id", type: "string" },
          { field: "business_address", as: "vendor_address", type: "string" },
          { field: "contact_name", as: "vendor_contact_name", type: "string" },
          {
            field: "contact_email",
            as: "vendor_contact_email",
            type: "string",
          },
          {
            field: "contact_phone",
            as: "vendor_contact_phone",
            type: "string",
          },
          {
            field: "business_type",
            as: "vendor_business_type",
            type: "string",
          },
          {
            field: "revenue_range",
            as: "vendor_revenue_range",
            type: "string",
          },
          { field: "services_offered", as: "vendor_services", type: "string" },
          { field: "document_urls", as: "vendor_documents", type: "string" },
        ],
      ),

      // 3. AI tax document validator
      aiNode(
        "3",
        "AI validate tax document completeness",
        520,
        "You are a tax compliance validator. Analyze the vendor registration data and check: (1) Is the tax ID format valid for the business type (EIN format: XX-XXXXXXX)? (2) Does the legal entity name match typical conventions? (3) Are all required W-9 fields present? (4) Is the business address complete with state and ZIP? Return JSON: {tax_id_format_valid, entity_name_valid, w9_complete, address_complete, overall_valid, validation_notes, risk_level}",
        "Company: {vendor_company}\\nLegal Entity: {vendor_legal_entity}\\nTax ID: {vendor_tax_id}\\nAddress: {vendor_address}\\nBusiness Type: {vendor_business_type}\\nDocuments: {vendor_documents}",
        [
          {
            field: "tax_id_format_valid",
            as: "tax_id_format_ok",
            type: "string",
          },
          { field: "overall_valid", as: "overall_validation", type: "string" },
          { field: "validation_notes", as: "validation_notes", type: "string" },
          { field: "risk_level", as: "vendor_risk_level", type: "string" },
        ],
      ),

      // 4. IRS TIN verification API
      n("4", "hitApi", "Verify tax ID against IRS TIN matching API", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.tax-id-pro.com/api/v1/tin/verify",
          headers: [
            {
              key: "Authorization",
              value: "Bearer {tax_verification_api_key}",
            },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "tin", value: "{vendor_tax_id}" },
            { key: "name", value: "{vendor_legal_entity}" },
          ],
          variables: [
            {
              name: "result.tin_valid",
              customName: "tin_verified",
              type: "string",
            },
            {
              name: "result.name_match",
              customName: "tin_name_match",
              type: "string",
            },
            {
              name: "result.verification_code",
              customName: "tin_verification_code",
              type: "string",
            },
          ],
        },
      }),

      // 5. Validation gate condition
      n(
        "5",
        "condition",
        "Check if vendor passes all validation checks",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "tin_verified", operator: "equals", value: "true" },
              {
                variable: "overall_validation",
                operator: "equals",
                value: "true",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Vendor profile creator HubSpot (True path)
      hubspotPostNode(
        "6",
        "Create vendor profile in HubSpot CRM",
        1240,
        "crm/v3/objects/companies",
        [
          {
            key: "properties",
            value:
              '{"name":"{vendor_company}","domain":"{vendor_contact_email}","phone":"{vendor_contact_phone}","address":"{vendor_address}","type":"VENDOR","description":"Services: {vendor_services}. Revenue range: {vendor_revenue_range}. Risk: {vendor_risk_level}. TIN verified: {tin_verification_code}","hs_lead_status":"VENDOR_ONBOARDING"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_vendor_id", type: "string" },
          {
            name: "properties.name",
            customName: "hubspot_vendor_name",
            type: "string",
          },
        ],
      ),

      // 7. ERP vendor sync API
      n("7", "hitApi", "Sync vendor to ERP accounting system", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{erp_domain}/api/v2/vendors",
          headers: [
            { key: "Authorization", value: "Bearer {erp_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vendor_name", value: "{vendor_company}" },
            { key: "legal_name", value: "{vendor_legal_entity}" },
            { key: "tax_id", value: "{vendor_tax_id}" },
            { key: "address", value: "{vendor_address}" },
            { key: "contact_email", value: "{vendor_contact_email}" },
            { key: "payment_terms", value: "NET30" },
            { key: "external_id", value: "{hubspot_vendor_id}" },
          ],
          variables: [
            { name: "id", customName: "erp_vendor_id", type: "string" },
            {
              name: "vendor_code",
              customName: "erp_vendor_code",
              type: "string",
            },
          ],
        },
      }),

      // 8. Welcome packet email
      emailNode(
        "8",
        "Send vendor welcome packet email",
        1720,
        "{vendor_contact_email}",
        "Welcome to our Vendor Program - {vendor_company}",
        "Dear {vendor_contact_name},\\n\\nWelcome to our Approved Vendor Program! Your registration has been verified and approved.\\n\\nVendor Details:\\n- Company: {vendor_company}\\n- Vendor Code: {erp_vendor_code}\\n- HubSpot ID: {hubspot_vendor_id}\\n- Payment Terms: NET30\\n\\nNext Steps:\\n1. Complete your vendor profile in our portal\\n2. Upload insurance certificates\\n3. Review and sign our Master Services Agreement\\n4. Submit banking details for payment setup\\n\\nIf you have questions, contact our procurement team.\\n\\nBest regards,\\nVendor Management Team",
      ),

      // 9. Procurement team Slack notification
      slackNode(
        "9",
        "Notify procurement team of new approved vendor",
        1960,
        ":white_check_mark: *New Vendor Approved*\\n>*Company:* {vendor_company}\\n>*Contact:* {vendor_contact_name} ({vendor_contact_email})\\n>*Services:* {vendor_services}\\n>*Revenue Range:* {vendor_revenue_range}\\n>*Risk Level:* {vendor_risk_level}\\n>*TIN Verification:* {tin_verification_code}\\n>*HubSpot ID:* {hubspot_vendor_id}\\n>*ERP Code:* {erp_vendor_code}",
      ),

      // 10. Compliance document request email (False path)
      emailNode(
        "10",
        "Send compliance document request for failed validation",
        1240,
        "{vendor_contact_email}",
        "Action Required: Additional Documentation Needed - {vendor_company}",
        "Dear {vendor_contact_name},\\n\\nThank you for registering {vendor_company} with our vendor program. During our review, we identified items requiring your attention:\\n\\nValidation Notes: {validation_notes}\\nTax ID Verification Result: {tin_verification_code}\\nName Match Status: {tin_name_match}\\n\\nPlease provide the following:\\n1. Updated W-9 form with correct information\\n2. Copy of your IRS Letter 147C (if tax ID mismatch)\\n3. Certificate of Good Standing from your state of incorporation\\n\\nPlease resubmit within 10 business days to continue the onboarding process.\\n\\nBest regards,\\nVendor Compliance Team",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "10", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1450. VISA REQUIREMENT CHECKER BOT (8 nodes)
  // ===================================================================

  "Visa Requirement Checker Bot": {
    summary:
      "Receives traveler inquiry webhook, AI-extracts nationality and destination, looks up visa requirements via API, classifies eligibility, AI-generates advisory response, handles appointment booking for visa-required travelers, sends document checklist email, and escalates complex cases to human agents via Slack.",
    nodes: [
      trigger("Webhook: Traveler visa inquiry submitted", "webhook_app", {
        source: "travel_portal",
        event: "visa.inquiry",
        howItStarts:
          "Fires when a traveler submits a visa inquiry through the travel portal or chatbot, sending the raw inquiry payload with traveler nationality, destination, travel dates, and purpose",
        setup:
          "Configure travel portal to POST visa inquiries to this URL. Set up visa requirements API access (Sherpa or VisaHQ). Configure appointment booking system integration.",
        inputFields: [],
      }),

      // 2. AI entity extraction
      aiNode(
        "2",
        "AI extract traveler and destination data from inquiry",
        280,
        "Extract traveler visa inquiry data from this raw webhook payload. Find the traveler full name, nationality/passport country, destination country, travel start date, travel end date, purpose of travel (tourism, business, education, work), passport expiration date, email address, and any special circumstances regardless of field naming format. Return JSON: {traveler_name, nationality, passport_country_code, destination_country, destination_country_code, travel_start, travel_end, travel_purpose, passport_expiry, traveler_email, special_circumstances}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "traveler_name", as: "traveler_name", type: "string" },
          { field: "nationality", as: "traveler_nationality", type: "string" },
          {
            field: "passport_country_code",
            as: "passport_code",
            type: "string",
          },
          {
            field: "destination_country",
            as: "destination_country",
            type: "string",
          },
          {
            field: "destination_country_code",
            as: "destination_code",
            type: "string",
          },
          { field: "travel_start", as: "travel_start_date", type: "string" },
          { field: "travel_end", as: "travel_end_date", type: "string" },
          { field: "travel_purpose", as: "travel_purpose", type: "string" },
          {
            field: "passport_expiry",
            as: "passport_expiry_date",
            type: "string",
          },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          {
            field: "special_circumstances",
            as: "special_notes",
            type: "string",
          },
        ],
      ),

      // 3. Visa requirements lookup API
      n(
        "3",
        "hitApi",
        "Lookup visa requirements for nationality-destination pair",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.sherpa.ai/v2/entry-requirements?nationality={passport_code}&destination={destination_code}&travelPurpose={travel_purpose}",
            headers: [
              { key: "Authorization", value: "Bearer {sherpa_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "data.visaRequired",
                customName: "visa_required",
                type: "string",
              },
              {
                name: "data.visaType",
                customName: "visa_type",
                type: "string",
              },
              {
                name: "data.maxStayDays",
                customName: "max_stay_days",
                type: "number",
              },
              {
                name: "data.processingTimeDays",
                customName: "processing_days",
                type: "number",
              },
              {
                name: "data.requirements",
                customName: "requirement_list",
                type: "string",
              },
              {
                name: "data.fees.amount",
                customName: "visa_fee",
                type: "string",
              },
              {
                name: "data.passportValidityMonths",
                customName: "passport_validity_required",
                type: "number",
              },
            ],
          },
        },
      ),

      // 4. Eligibility classifier condition
      n("4", "condition", "Check if visa is required for this traveler", 760, {
        conditionDetails: {
          conditions: [
            { variable: "visa_required", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. AI advisory response (True path - visa required)
      aiNode(
        "5",
        "AI generate visa advisory and preparation guide",
        1000,
        "You are a visa and immigration advisor. Based on the visa requirements data, generate a comprehensive advisory for this traveler. Include: visa type needed, estimated processing time, required documents, fees, passport validity requirements, and a timeline for preparation. Flag any issues like passport expiring too soon. Return JSON: {advisory_summary, visa_type_needed, document_checklist, estimated_timeline, fee_breakdown, passport_warning, preparation_steps, complexity_level}",
        "Traveler: {traveler_name} ({traveler_nationality})\\nDestination: {destination_country}\\nPurpose: {travel_purpose}\\nTravel dates: {travel_start_date} to {travel_end_date}\\nPassport expiry: {passport_expiry_date}\\nVisa type: {visa_type}\\nMax stay: {max_stay_days} days\\nProcessing time: {processing_days} days\\nRequirements: {requirement_list}\\nFee: {visa_fee}\\nPassport validity needed: {passport_validity_required} months\\nSpecial notes: {special_notes}",
        [
          { field: "advisory_summary", as: "visa_advisory", type: "string" },
          {
            field: "document_checklist",
            as: "document_checklist",
            type: "string",
          },
          {
            field: "estimated_timeline",
            as: "preparation_timeline",
            type: "string",
          },
          { field: "fee_breakdown", as: "fee_details", type: "string" },
          { field: "passport_warning", as: "passport_warning", type: "string" },
          { field: "complexity_level", as: "case_complexity", type: "string" },
        ],
      ),

      // 6. Appointment booking handler API
      n(
        "6",
        "hitApi",
        "Create visa appointment booking in scheduling system",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.calendly.com/scheduling_links",
            headers: [
              { key: "Authorization", value: "Bearer {calendly_api_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "max_event_count", value: "1" },
              { key: "owner", value: "{calendly_visa_advisor_uri}" },
              { key: "owner_type", value: "EventType" },
            ],
            variables: [
              {
                name: "resource.booking_url",
                customName: "booking_url",
                type: "string",
              },
              {
                name: "resource.owner",
                customName: "advisor_uri",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Document checklist email
      emailNode(
        "7",
        "Send visa document checklist and advisory email",
        1480,
        "{traveler_email}",
        "Your Visa Advisory: {destination_country} - {visa_type} Required",
        "Dear {traveler_name},\\n\\nBased on your travel plans to {destination_country} ({travel_start_date} to {travel_end_date}), here is your visa advisory:\\n\\n{visa_advisory}\\n\\nVisa Type: {visa_type}\\nEstimated Fee: {fee_details}\\nProcessing Time: {processing_days} days\\n\\nRequired Documents:\\n{document_checklist}\\n\\nPreparation Timeline:\\n{preparation_timeline}\\n\\n{passport_warning}\\n\\nBook a consultation with our visa advisor: {booking_url}\\n\\nBest regards,\\nTravel Visa Advisory Team",
      ),

      // 8. Human escalation Slack (also for visa-free path)
      slackNode(
        "8",
        "Escalate complex visa case to human advisor",
        1000,
        ":passport_control: *Visa Inquiry - {case_complexity} Complexity*\\n>*Traveler:* {traveler_name} ({traveler_nationality})\\n>*Destination:* {destination_country}\\n>*Purpose:* {travel_purpose}\\n>*Dates:* {travel_start_date} to {travel_end_date}\\n>*Visa Required:* {visa_required}\\n>*Visa Type:* {visa_type}\\n>*Special Notes:* {special_notes}\\n>*Passport Warning:* {passport_warning}\\n>*Booking Link:* {booking_url}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1451. WEBHOOK EVENT ROUTER (8 nodes)
  // ===================================================================

  "Webhook Event Router": {
    summary:
      "Receives universal webhook events, normalizes payload with runCode, validates HMAC signatures, routes by event type (CRM vs payment), processes CRM events via HubSpot, processes payment events via Stripe API, queues unrecognized events to dead letter Sheets, and logs all events for audit.",
    nodes: [
      trigger("Webhook: Universal event ingestion endpoint", "webhook_app", {
        source: "universal_router",
        event: "event.received",
        howItStarts:
          "Fires when any external service sends an event to the universal webhook endpoint, accepting raw payloads from CRM, payment, notification, and other systems",
        setup:
          "Configure all external services to POST events to this universal URL. Store HMAC signing secrets for each source. Configure HubSpot and Stripe API tokens. Prepare dead letter queue spreadsheet.",
        inputFields: [],
      }),

      // 2. Payload normalizer
      n(
        "2",
        "runCode",
        "Normalize webhook payload to standard event format",
        280,
        {
          runCodeDetails: {
            code: `const raw = variables.webhook_payload || '{}';
let parsed;
try { parsed = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch(e) { parsed = { raw_body: raw }; }
const headers = variables.webhook_headers || '{}';
let headersParsed;
try { headersParsed = typeof headers === 'string' ? JSON.parse(headers) : headers; } catch(e) { headersParsed = {}; }
output.event_source = parsed.source || parsed.provider || parsed.app || headersParsed['x-webhook-source'] || 'unknown';
output.event_type = parsed.event || parsed.type || parsed.event_type || parsed.action || 'unknown';
output.event_id = parsed.id || parsed.event_id || parsed.message_id || Date.now().toString();
output.event_timestamp = parsed.timestamp || parsed.created_at || parsed.occurred_at || new Date().toISOString();
output.event_payload = JSON.stringify(parsed);
output.signature_header = headersParsed['x-hub-signature-256'] || headersParsed['stripe-signature'] || headersParsed['x-signature'] || '';
output.content_type = headersParsed['content-type'] || 'application/json';`,
            outputVariables: [
              { name: "event_source", type: "string" },
              { name: "event_type", type: "string" },
              { name: "event_id", type: "string" },
              { name: "event_timestamp", type: "string" },
              { name: "event_payload", type: "string" },
              { name: "signature_header", type: "string" },
              { name: "content_type", type: "string" },
            ],
          },
        },
      ),

      // 3. Signature authentication validator
      n(
        "3",
        "runCode",
        "Validate webhook HMAC signature for authenticity",
        520,
        {
          runCodeDetails: {
            code: `const crypto = require('crypto');
const signature = variables.signature_header || '';
const payload = variables.event_payload || '';
const source = variables.event_source || '';
let secretKey = '';
let isValid = false;
const secretMap = { hubspot: '{hubspot_webhook_secret}', stripe: '{stripe_webhook_secret}', github: '{github_webhook_secret}' };
secretKey = secretMap[source.toLowerCase()] || '';
if (signature && secretKey) {
  const computed = crypto.createHmac('sha256', secretKey).update(payload).digest('hex');
  isValid = signature.includes(computed);
} else if (!signature) {
  isValid = true; // No signature expected for this source
}
output.signature_valid = isValid;
output.auth_method = signature ? 'hmac_sha256' : 'none';
output.event_category = (variables.event_type || '').includes('contact') || (variables.event_type || '').includes('deal') || (variables.event_type || '').includes('company') ? 'crm' : (variables.event_type || '').includes('payment') || (variables.event_type || '').includes('invoice') || (variables.event_type || '').includes('charge') || (variables.event_type || '').includes('subscription') ? 'payment' : 'other';`,
            outputVariables: [
              { name: "signature_valid", type: "boolean" },
              { name: "auth_method", type: "string" },
              { name: "event_category", type: "string" },
            ],
          },
        },
      ),

      // 4. Event type router condition
      n(
        "4",
        "condition",
        "Route event by category: CRM vs payment vs other",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "event_category", operator: "equals", value: "crm" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. CRM event processor HubSpot (True path)
      hubspotPostNode(
        "5",
        "Process CRM event and update HubSpot contact",
        1000,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_object_id","operator":"HAS_PROPERTY"}]}]',
          },
          {
            key: "properties",
            value: '["email","firstname","lastname","hs_lead_status"]',
          },
          { key: "limit", value: "1" },
        ],
        [
          { name: "total", customName: "crm_records_affected", type: "number" },
          {
            name: "results[0].id",
            customName: "crm_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.email",
            customName: "crm_contact_email",
            type: "string",
          },
        ],
      ),

      // 6. Payment event processor Stripe API (False path)
      n(
        "6",
        "hitApi",
        "Process payment event via Stripe events API",
        1000,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/events/{event_id}",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              {
                name: "data.object.id",
                customName: "payment_object_id",
                type: "string",
              },
              {
                name: "data.object.amount",
                customName: "payment_amount",
                type: "number",
              },
              {
                name: "data.object.currency",
                customName: "payment_currency",
                type: "string",
              },
              {
                name: "data.object.status",
                customName: "payment_status",
                type: "string",
              },
            ],
          },
        },
        400,
      ),

      // 7. Dead letter queue for unrecognized events
      sheetsNode(
        "7",
        "Log unrecognized events to dead letter queue",
        1240,
        "DeadLetterQueue",
        `[["{event_id}", "{event_source}", "{event_type}", "{event_category}", "{event_timestamp}", "{signature_valid}", "{event_payload}"]]`,
      ),

      // 8. Event audit logger
      sheetsNode(
        "8",
        "Log all processed events to audit trail",
        1480,
        "EventAuditLog",
        `[["{event_id}", "{event_source}", "{event_type}", "{event_category}", "{event_timestamp}", "{auth_method}", "{signature_valid}", "processed"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1452. WEBINAR REGISTRATION BOT (8 nodes)
  // ===================================================================

  "Webinar Registration Bot": {
    summary:
      "Receives chat inquiry webhook, AI-extracts webinar intent, looks up upcoming webinars via API, classifies registration intent, registers attendee via API, sends confirmation email, updates CRM contact in HubSpot, and escalates complex inquiries to live agent via Slack.",
    nodes: [
      trigger("Webhook: Chat inquiry about webinar received", "webhook_app", {
        source: "chat_widget",
        event: "message.received",
        howItStarts:
          "Fires when a user sends a message through the chat widget asking about webinars, registrations, or upcoming events, sending the raw chat payload with user info and message text",
        setup:
          "Configure chat widget to POST messages to this URL. Set up webinar platform API (Zoom/GoToWebinar). Configure HubSpot for lead tracking. Prepare Slack webhook for live agent escalation.",
        inputFields: [],
      }),

      // 2. AI extract webinar intent
      aiNode(
        "2",
        "AI extract webinar intent and user data from chat",
        280,
        "Extract webinar inquiry data from this raw chat webhook payload. Find the user name, user email, message text, the specific webinar topic or date they asked about, and their intent (register, ask questions, get info, request recording). Return JSON: {user_name, user_email, message_text, webinar_topic, preferred_date, intent_type, urgency_level}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "user_name", as: "inquirer_name", type: "string" },
          { field: "user_email", as: "inquirer_email", type: "string" },
          { field: "message_text", as: "inquiry_message", type: "string" },
          { field: "webinar_topic", as: "requested_topic", type: "string" },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          { field: "intent_type", as: "inquiry_intent", type: "string" },
          { field: "urgency_level", as: "inquiry_urgency", type: "string" },
        ],
      ),

      // 3. Upcoming webinar lookup API
      n("3", "hitApi", "Fetch upcoming webinars from Zoom", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.zoom.us/v2/users/{zoom_user_id}/webinars?type=upcoming&page_size=10",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total_records",
              customName: "total_upcoming_webinars",
              type: "number",
            },
            {
              name: "webinars[0].id",
              customName: "next_webinar_id",
              type: "string",
            },
            {
              name: "webinars[0].topic",
              customName: "next_webinar_topic",
              type: "string",
            },
            {
              name: "webinars[0].start_time",
              customName: "next_webinar_date",
              type: "string",
            },
            {
              name: "webinars[0].duration",
              customName: "next_webinar_duration",
              type: "number",
            },
            {
              name: "webinars[0].join_url",
              customName: "next_webinar_url",
              type: "string",
            },
          ],
        },
      }),

      // 4. Registration intent classifier
      n(
        "4",
        "condition",
        "Check if user intends to register for a webinar",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "inquiry_intent",
                operator: "equals",
                value: "register",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Webinar registration API (True path)
      n("5", "hitApi", "Register attendee for Zoom webinar", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.zoom.us/v2/webinars/{next_webinar_id}/registrants",
          headers: [
            { key: "Authorization", value: "Bearer {zoom_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email", value: "{inquirer_email}" },
            { key: "first_name", value: "{inquirer_name}" },
          ],
          variables: [
            {
              name: "registrant_id",
              customName: "registrant_id",
              type: "string",
            },
            {
              name: "join_url",
              customName: "registrant_join_url",
              type: "string",
            },
            {
              name: "topic",
              customName: "registered_webinar_topic",
              type: "string",
            },
          ],
        },
      }),

      // 6. Confirmation email
      emailNode(
        "6",
        "Send webinar registration confirmation email",
        1240,
        "{inquirer_email}",
        "Confirmed: You're Registered for {registered_webinar_topic}",
        "Hi {inquirer_name},\\n\\nYou're all set! Here are your webinar details:\\n\\nWebinar: {registered_webinar_topic}\\nDate: {next_webinar_date}\\nDuration: {next_webinar_duration} minutes\\n\\nJoin Link: {registrant_join_url}\\n\\nAdd this to your calendar and we'll send a reminder 1 hour before the event.\\n\\nSee you there!\\nWebinar Team",
      ),

      // 7. CRM contact update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM contact with webinar registration",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{inquirer_email}","firstname":"{inquirer_name}","hs_lead_status":"WEBINAR_REGISTRANT","last_webinar_registered":"{registered_webinar_topic}","webinar_registration_date":"{next_webinar_date}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 8. Live agent escalation Slack (False path)
      slackNode(
        "8",
        "Escalate non-registration inquiry to live agent",
        1000,
        ":speech_balloon: *Webinar Inquiry Needs Human Attention*\\n>*From:* {inquirer_name} ({inquirer_email})\\n>*Intent:* {inquiry_intent}\\n>*Message:* {inquiry_message}\\n>*Topic Requested:* {requested_topic}\\n>*Preferred Date:* {preferred_date}\\n>*Upcoming Webinars:* {total_upcoming_webinars} available\\n>*Next Webinar:* {next_webinar_topic} on {next_webinar_date}",
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1453. WEBINAR REGISTRATION NURTURE SEQUENCE (9 nodes)
  // ===================================================================

  "Webinar Registration Nurture Sequence": {
    summary:
      "Receives webinar registration webhook, AI-extracts registrant data, enriches contact via Clearbit, AI-personalizes content, sends pre-webinar email 1, delays 3 days, sends pre-webinar email 2, sends day-of reminder via email and SMS, and updates lead score in HubSpot.",
    nodes: [
      trigger("Webhook: New webinar registration event", "webhook_app", {
        source: "zoom",
        event: "webinar.registration_created",
        howItStarts:
          "Fires when a new attendee registers for a webinar through Zoom, sending the raw registration payload with registrant details, webinar info, and registration metadata",
        setup:
          "Configure Zoom to POST webinar.registration_created webhooks to this URL. Set up Clearbit API for contact enrichment. Configure HubSpot for lead scoring. Prepare email nurture templates.",
        inputFields: [],
      }),

      // 2. AI extract registrant data
      aiNode(
        "2",
        "AI extract registrant and webinar details from webhook",
        280,
        "Extract webinar registration data from this raw Zoom webhook payload. Find the registrant email, first name, last name, company, job title, webinar ID, webinar topic, webinar start time, registration time, and join URL regardless of field naming format. Return JSON: {registrant_email, first_name, last_name, company, job_title, webinar_id, webinar_topic, webinar_start_time, registration_time, join_url}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "registrant_email", as: "registrant_email", type: "string" },
          { field: "first_name", as: "registrant_first_name", type: "string" },
          { field: "last_name", as: "registrant_last_name", type: "string" },
          { field: "company", as: "registrant_company", type: "string" },
          { field: "job_title", as: "registrant_title", type: "string" },
          { field: "webinar_id", as: "webinar_id", type: "string" },
          { field: "webinar_topic", as: "webinar_topic", type: "string" },
          {
            field: "webinar_start_time",
            as: "webinar_start_time",
            type: "string",
          },
          {
            field: "registration_time",
            as: "registration_timestamp",
            type: "string",
          },
          { field: "join_url", as: "webinar_join_url", type: "string" },
        ],
      ),

      // 3. Contact enrichment Clearbit
      n("3", "hitApi", "Enrich registrant contact via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/combined/find?email={registrant_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "person.name.fullName",
              customName: "enriched_name",
              type: "string",
            },
            {
              name: "company.name",
              customName: "enriched_company",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "company.metrics.employeesRange",
              customName: "company_size",
              type: "string",
            },
            {
              name: "person.employment.title",
              customName: "enriched_title",
              type: "string",
            },
            {
              name: "company.domain",
              customName: "company_domain",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI content personalizer
      aiNode(
        "4",
        "AI personalize nurture content for registrant",
        760,
        "You are a marketing content personalizer. Based on the registrant profile and webinar topic, create personalized email content for a 3-email nurture sequence. Email 1: value proposition + what they will learn. Email 2: speaker highlights + agenda preview. Email 3: day-of reminder + bonus resource. Tailor to their industry and role. Return JSON: {email1_subject, email1_body, email2_subject, email2_body, email3_subject, email3_body, sms_reminder, personalization_notes}",
        "Registrant: {registrant_first_name} {registrant_last_name}\\nTitle: {enriched_title}\\nCompany: {enriched_company}\\nIndustry: {company_industry}\\nCompany Size: {company_size}\\nWebinar: {webinar_topic}\\nStart Time: {webinar_start_time}",
        [
          {
            field: "email1_subject",
            as: "nurture_email1_subject",
            type: "string",
          },
          { field: "email1_body", as: "nurture_email1_body", type: "string" },
          {
            field: "email2_subject",
            as: "nurture_email2_subject",
            type: "string",
          },
          { field: "email2_body", as: "nurture_email2_body", type: "string" },
          {
            field: "email3_subject",
            as: "nurture_email3_subject",
            type: "string",
          },
          { field: "email3_body", as: "nurture_email3_body", type: "string" },
          { field: "sms_reminder", as: "sms_reminder_text", type: "string" },
        ],
      ),

      // 5. Pre-webinar email 1
      emailNode(
        "5",
        "Send pre-webinar nurture email 1: Value proposition",
        1000,
        "{registrant_email}",
        "{nurture_email1_subject}",
        "Hi {registrant_first_name},\\n\\n{nurture_email1_body}\\n\\nYour webinar join link: {webinar_join_url}\\n\\nSee you at {webinar_topic}!\\nThe Webinar Team",
      ),

      // 6. Delay 3 days
      n(
        "6",
        "delayAction",
        "Wait 3 days before sending second nurture email",
        1240,
        {
          delayDetails: { duration: 3, unit: "days" },
        },
      ),

      // 7. Pre-webinar email 2
      emailNode(
        "7",
        "Send pre-webinar nurture email 2: Agenda preview",
        1480,
        "{registrant_email}",
        "{nurture_email2_subject}",
        "Hi {registrant_first_name},\\n\\n{nurture_email2_body}\\n\\nRemember to save your spot: {webinar_join_url}\\n\\nLooking forward to seeing you!\\nThe Webinar Team",
      ),

      // 8. Day-of reminder email + SMS
      emailNode(
        "8",
        "Send day-of webinar reminder email and SMS",
        1720,
        "{registrant_email}",
        "{nurture_email3_subject}",
        "Hi {registrant_first_name},\\n\\n{nurture_email3_body}\\n\\nJoin now: {webinar_join_url}\\n\\nWe are excited to have you!\\nThe Webinar Team",
      ),

      // 9. Lead score update HubSpot
      hubspotPostNode(
        "9",
        "Update lead score in HubSpot for webinar engagement",
        1960,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{registrant_email}","firstname":"{registrant_first_name}","lastname":"{registrant_last_name}","company":"{enriched_company}","jobtitle":"{enriched_title}","industry":"{company_industry}","hs_lead_status":"WEBINAR_NURTURED","last_webinar":"{webinar_topic}","numemployees":"{company_size}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1454. WEBSITE DOWNTIME MONITOR (7 nodes)
  // ===================================================================

  "Website Downtime Monitor": {
    summary:
      "Runs every 5 minutes to HTTP health-check the website, evaluates status with runCode, alerts Slack on downtime, sends email alert on downtime, detects recovery transitions, and logs all uptime metrics to Sheets.",
    nodes: [
      trigger("Schedule: Website health check every 5 minutes", "schedule", {
        schedule: "Every 5 minutes",
        howItStarts:
          "Runs every 5 minutes to perform an HTTP health check against the configured website endpoint, monitoring response status, latency, and availability",
        setup:
          "Configure the target website URL. Set up Slack webhook for #ops-alerts channel. Prepare Google Sheets for uptime metrics tracking. Set acceptable latency thresholds.",
        inputFields: [],
      }),

      // 2. HTTP health check API
      n("2", "hitApi", "Perform HTTP health check on website endpoint", 280, {
        hitApiDetails: {
          method: "GET",
          url: "{monitored_website_url}/health",
          headers: [{ key: "User-Agent", value: "OmniEngage-HealthCheck/1.0" }],
          body: [],
          variables: [
            { name: "status", customName: "http_status_code", type: "number" },
            {
              name: "statusText",
              customName: "http_status_text",
              type: "string",
            },
            {
              name: "headers.x-response-time",
              customName: "response_time_ms",
              type: "number",
            },
            {
              name: "data.status",
              customName: "health_status",
              type: "string",
            },
            { name: "data.version", customName: "app_version", type: "string" },
          ],
        },
      }),

      // 3. Status evaluator
      n("3", "runCode", "Evaluate website health status and latency", 520, {
        runCodeDetails: {
          code: `const statusCode = parseInt(variables.http_status_code) || 0;
const responseTime = parseInt(variables.response_time_ms) || 9999;
const healthStatus = variables.health_status || 'unknown';
const maxLatencyMs = 3000;
const isUp = statusCode >= 200 && statusCode < 400 && responseTime < maxLatencyMs;
const isDegraded = statusCode >= 200 && statusCode < 400 && responseTime >= maxLatencyMs;
output.site_is_up = isUp;
output.site_is_degraded = isDegraded;
output.site_is_down = !isUp && !isDegraded;
output.status_label = isUp ? 'HEALTHY' : isDegraded ? 'DEGRADED' : 'DOWN';
output.check_timestamp = new Date().toISOString();
output.latency_ok = responseTime < maxLatencyMs;
output.alert_needed = !isUp;`,
          outputVariables: [
            { name: "site_is_up", type: "boolean" },
            { name: "site_is_degraded", type: "boolean" },
            { name: "site_is_down", type: "boolean" },
            { name: "status_label", type: "string" },
            { name: "check_timestamp", type: "string" },
            { name: "latency_ok", type: "boolean" },
            { name: "alert_needed", type: "boolean" },
          ],
        },
      }),

      // 4. Downtime alert condition
      n(
        "4",
        "condition",
        "Check if site is down or degraded and alert needed",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "alert_needed", operator: "equals", value: "true" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Downtime Slack alert (True path)
      slackNode(
        "5",
        "Send downtime alert to Slack ops channel",
        1000,
        ":rotating_light: *WEBSITE DOWN/DEGRADED*\\n>*Status:* {status_label}\\n>*HTTP Code:* {http_status_code} {http_status_text}\\n>*Response Time:* {response_time_ms}ms\\n>*Health Endpoint:* {health_status}\\n>*App Version:* {app_version}\\n>*Checked At:* {check_timestamp}\\n>\\n>*URL:* {monitored_website_url}/health\\n>Investigate immediately. Check server logs and infrastructure dashboards.",
      ),

      // 6. Downtime email alert
      emailNode(
        "6",
        "Send downtime alert email to engineering team",
        1240,
        "{engineering_team_email}",
        "ALERT: Website {status_label} - {http_status_code} at {check_timestamp}",
        "Website Health Alert\\n\\nStatus: {status_label}\\nHTTP Status: {http_status_code} {http_status_text}\\nResponse Time: {response_time_ms}ms\\nHealth Endpoint Response: {health_status}\\nApp Version: {app_version}\\nTimestamp: {check_timestamp}\\n\\nURL Checked: {monitored_website_url}/health\\n\\nPlease investigate immediately and check:\\n1. Server process status\\n2. Database connectivity\\n3. Load balancer health\\n4. Recent deployments\\n\\n- Automated Monitoring System",
      ),

      // 7. Uptime metrics logger (both paths converge here)
      sheetsNode(
        "7",
        "Log health check metrics to uptime tracker",
        1000,
        "UptimeMetrics",
        `[["{check_timestamp}", "{monitored_website_url}", "{http_status_code}", "{response_time_ms}", "{status_label}", "{health_status}", "{app_version}", "{alert_needed}"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1455. WEBSITE FORM TO DATABASE PIPELINE (8 nodes)
  // ===================================================================

  "Website Form to Database Pipeline": {
    summary:
      "Receives form submission webhook, sanitizes input with runCode, checks for duplicates in HubSpot, resolves conflicts via condition, maps fields with runCode, upserts record to HubSpot, sends downstream notification email, and logs errors to Sheets.",
    nodes: [
      trigger("Webhook: Website form submission received", "webhook_form", {
        source: "website_form",
        event: "form.submitted",
        howItStarts:
          "Fires when a visitor submits any form on the website (contact, demo request, newsletter), sending the raw form data payload with all field values and metadata",
        setup:
          "Configure website forms to POST submissions to this URL. Set up HubSpot for CRM storage. Prepare error logging spreadsheet. Configure notification email recipients.",
        inputFields: [],
      }),

      // 2. Input sanitizer
      n("2", "runCode", "Sanitize and validate form input fields", 280, {
        runCodeDetails: {
          code: `const raw = variables.webhook_payload || '{}';
let data;
try { data = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch(e) { data = {}; }
function sanitize(str) {
  if (!str) return '';
  return String(str).replace(/<[^>]*>/g, '').replace(/[<>'";\\/]/g, '').trim().substring(0, 500);
}
output.clean_email = sanitize(data.email || data.Email || data.user_email || '').toLowerCase();
output.clean_name = sanitize(data.name || data.full_name || data.first_name || '');
output.clean_last_name = sanitize(data.last_name || data.lastname || data.surname || '');
output.clean_company = sanitize(data.company || data.organization || data.company_name || '');
output.clean_phone = sanitize(data.phone || data.phone_number || data.tel || '');
output.clean_message = sanitize(data.message || data.comments || data.notes || '');
output.form_type = sanitize(data.form_type || data.form_id || data.form_name || 'unknown');
output.submission_page = sanitize(data.page_url || data.referrer || data.source_url || '');
output.is_valid_email = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/.test(output.clean_email);
output.validation_passed = output.is_valid_email && output.clean_name.length > 0;`,
          outputVariables: [
            { name: "clean_email", type: "string" },
            { name: "clean_name", type: "string" },
            { name: "clean_last_name", type: "string" },
            { name: "clean_company", type: "string" },
            { name: "clean_phone", type: "string" },
            { name: "clean_message", type: "string" },
            { name: "form_type", type: "string" },
            { name: "submission_page", type: "string" },
            { name: "is_valid_email", type: "boolean" },
            { name: "validation_passed", type: "boolean" },
          ],
        },
      }),

      // 3. Duplicate detection HubSpot
      hubspotPostNode(
        "3",
        "Search HubSpot for existing contact by email",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{clean_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","hs_lead_status","createdate"]',
          },
        ],
        [
          {
            name: "total",
            customName: "existing_contact_count",
            type: "number",
          },
          {
            name: "results[0].id",
            customName: "existing_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "existing_lead_status",
            type: "string",
          },
          {
            name: "results[0].properties.createdate",
            customName: "existing_created_date",
            type: "string",
          },
        ],
      ),

      // 4. Conflict resolution condition
      n("4", "condition", "Check if contact already exists in CRM", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "existing_contact_count",
              operator: "greater_than",
              value: "0",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Field mapping engine (runs for both paths)
      n("5", "runCode", "Map and merge form fields for CRM upsert", 1000, {
        runCodeDetails: {
          code: `const isUpdate = parseInt(variables.existing_contact_count) > 0;
const contactId = variables.existing_contact_id || '';
output.upsert_method = isUpdate ? 'PATCH' : 'POST';
output.upsert_endpoint = isUpdate ? 'crm/v3/objects/contacts/' + contactId : 'crm/v3/objects/contacts';
output.mapped_properties = JSON.stringify({
  email: variables.clean_email,
  firstname: variables.clean_name,
  lastname: variables.clean_last_name,
  company: variables.clean_company,
  phone: variables.clean_phone,
  hs_lead_status: isUpdate ? variables.existing_lead_status : 'NEW',
  last_form_submission: variables.form_type,
  last_form_message: variables.clean_message,
  last_form_page: variables.submission_page,
  last_form_date: new Date().toISOString()
});
output.action_label = isUpdate ? 'Updated existing contact ' + contactId : 'Created new contact';`,
          outputVariables: [
            { name: "upsert_method", type: "string" },
            { name: "upsert_endpoint", type: "string" },
            { name: "mapped_properties", type: "string" },
            { name: "action_label", type: "string" },
          ],
        },
      }),

      // 6. Database upsert HubSpot
      hubspotPostNode(
        "6",
        "Upsert contact record in HubSpot CRM",
        1240,
        "{upsert_endpoint}",
        [{ key: "properties", value: "{mapped_properties}" }],
        [
          { name: "id", customName: "upserted_contact_id", type: "string" },
          {
            name: "properties.email",
            customName: "upserted_email",
            type: "string",
          },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),

      // 7. Downstream notification email
      emailNode(
        "7",
        "Send form submission notification to team",
        1480,
        "{sales_team_email}",
        "New Form Submission: {form_type} from {clean_name} at {clean_company}",
        "New form submission received:\\n\\nForm: {form_type}\\nPage: {submission_page}\\n\\nContact Details:\\n- Name: {clean_name} {clean_last_name}\\n- Email: {clean_email}\\n- Company: {clean_company}\\n- Phone: {clean_phone}\\n\\nMessage: {clean_message}\\n\\nCRM Action: {action_label}\\nHubSpot Contact ID: {upserted_contact_id}\\n\\n- Form Pipeline Automation",
      ),

      // 8. Error queue
      sheetsNode(
        "8",
        "Log validation failures to error queue",
        1000,
        "FormErrorQueue",
        `[["{check_timestamp}", "{clean_email}", "{clean_name}", "{form_type}", "{is_valid_email}", "{validation_passed}", "{submission_page}"]]`,
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1456. WEEKLY REPORT GENERATOR (9 nodes)
  // ===================================================================

  "Weekly Report Generator": {
    summary:
      "Runs weekly to fetch sales data from HubSpot, marketing metrics from API, support metrics from API, AI-generates insights summary, generates chart visualizations with runCode, builds the full report document with runCode, distributes report via email, and archives to Sheets.",
    nodes: [
      trigger("Schedule: Weekly business report generation", "schedule", {
        schedule: "Every Monday at 7:00 AM",
        howItStarts:
          "Runs every Monday at 7 AM to gather metrics from sales, marketing, and support systems, compile insights, and distribute the weekly business performance report",
        setup:
          "Configure HubSpot API token for sales pipeline data. Set up marketing analytics API (Google Analytics or HubSpot Marketing). Configure support platform API (Zendesk/Intercom). Prepare distribution list.",
        inputFields: [],
      }),

      // 2. Sales data fetch from HubSpot
      hubspotGetNode(
        "2",
        "Fetch weekly sales pipeline data from HubSpot",
        280,
        'crm/v3/objects/deals?properties=dealname,amount,dealstage,closedate,hs_lastmodifieddate,pipeline&filterGroups=[{"filters":[{"propertyName":"hs_lastmodifieddate","operator":"GTE","value":"{seven_days_ago_epoch}"}]}]&limit=100',
        [
          {
            name: "total",
            customName: "total_deals_this_week",
            type: "number",
          },
          { name: "results", customName: "deals_data", type: "string" },
        ],
      ),

      // 3. Marketing metrics fetch API
      n("3", "hitApi", "Fetch weekly marketing analytics metrics", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/analytics/v2/reports/{hubspot_portal_id}/summarize/weekly?start={seven_days_ago}&end={today}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "visits", customName: "weekly_visits", type: "number" },
            {
              name: "contacts",
              customName: "weekly_new_contacts",
              type: "number",
            },
            { name: "leads", customName: "weekly_new_leads", type: "number" },
            {
              name: "bounceRate",
              customName: "weekly_bounce_rate",
              type: "string",
            },
            {
              name: "pageviews",
              customName: "weekly_pageviews",
              type: "number",
            },
          ],
        },
      }),

      // 4. Support metrics fetch API
      n(
        "4",
        "hitApi",
        "Fetch weekly support ticket metrics from Zendesk",
        760,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket%20created>{seven_days_ago}",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "count",
                customName: "weekly_tickets_created",
                type: "number",
              },
              {
                name: "results.length",
                customName: "tickets_in_page",
                type: "number",
              },
            ],
          },
        },
      ),

      // 5. AI insights summarizer
      aiNode(
        "5",
        "AI generate weekly business insights and trends",
        1000,
        "You are a business intelligence analyst. Analyze the weekly metrics from sales, marketing, and support. Identify key trends, highlight wins and concerns, provide week-over-week comparisons where possible, and give actionable recommendations. Return JSON: {executive_summary, sales_highlights, marketing_highlights, support_highlights, key_wins, areas_of_concern, recommendations, overall_health_score}",
        "Sales: {total_deals_this_week} deals active this week. Raw data: {deals_data}\\n\\nMarketing: {weekly_visits} visits, {weekly_new_contacts} new contacts, {weekly_new_leads} new leads, {weekly_bounce_rate} bounce rate, {weekly_pageviews} pageviews\\n\\nSupport: {weekly_tickets_created} tickets created this week",
        [
          {
            field: "executive_summary",
            as: "weekly_executive_summary",
            type: "string",
          },
          { field: "sales_highlights", as: "sales_highlights", type: "string" },
          {
            field: "marketing_highlights",
            as: "marketing_highlights",
            type: "string",
          },
          {
            field: "support_highlights",
            as: "support_highlights",
            type: "string",
          },
          { field: "key_wins", as: "key_wins", type: "string" },
          { field: "areas_of_concern", as: "areas_of_concern", type: "string" },
          {
            field: "recommendations",
            as: "weekly_recommendations",
            type: "string",
          },
          { field: "overall_health_score", as: "health_score", type: "string" },
        ],
      ),

      // 6. Chart visualization generator
      n("6", "runCode", "Generate metric visualization data for report", 1240, {
        runCodeDetails: {
          code: `const visits = parseInt(variables.weekly_visits) || 0;
const contacts = parseInt(variables.weekly_new_contacts) || 0;
const leads = parseInt(variables.weekly_new_leads) || 0;
const deals = parseInt(variables.total_deals_this_week) || 0;
const tickets = parseInt(variables.weekly_tickets_created) || 0;
const conversionRate = visits > 0 ? ((contacts / visits) * 100).toFixed(2) : '0.00';
const leadToContactRate = contacts > 0 ? ((leads / contacts) * 100).toFixed(2) : '0.00';
output.conversion_rate = conversionRate + '%';
output.lead_contact_rate = leadToContactRate + '%';
output.funnel_summary = 'Visits: ' + visits + ' → Contacts: ' + contacts + ' (' + conversionRate + '%) → Leads: ' + leads + ' (' + leadToContactRate + '%) → Deals: ' + deals;
output.support_load = tickets + ' tickets this week';
output.report_period = 'Week ending ' + new Date().toISOString().split('T')[0];`,
          outputVariables: [
            { name: "conversion_rate", type: "string" },
            { name: "lead_contact_rate", type: "string" },
            { name: "funnel_summary", type: "string" },
            { name: "support_load", type: "string" },
            { name: "report_period", type: "string" },
          ],
        },
      }),

      // 7. Report document builder
      n("7", "runCode", "Compile full weekly report document", 1480, {
        runCodeDetails: {
          code: `output.full_report = '=== WEEKLY BUSINESS REPORT ===\\n' +
'Period: ' + variables.report_period + '\\n' +
'Health Score: ' + variables.health_score + '\\n\\n' +
'--- EXECUTIVE SUMMARY ---\\n' + variables.weekly_executive_summary + '\\n\\n' +
'--- SALES ---\\n' + variables.sales_highlights + '\\nDeals Active: ' + variables.total_deals_this_week + '\\n\\n' +
'--- MARKETING ---\\n' + variables.marketing_highlights + '\\nFunnel: ' + variables.funnel_summary + '\\nConversion Rate: ' + variables.conversion_rate + '\\n\\n' +
'--- SUPPORT ---\\n' + variables.support_highlights + '\\nTicket Volume: ' + variables.support_load + '\\n\\n' +
'--- KEY WINS ---\\n' + variables.key_wins + '\\n\\n' +
'--- AREAS OF CONCERN ---\\n' + variables.areas_of_concern + '\\n\\n' +
'--- RECOMMENDATIONS ---\\n' + variables.weekly_recommendations;
output.report_subject = 'Weekly Business Report - ' + variables.report_period + ' (Health: ' + variables.health_score + ')';`,
          outputVariables: [
            { name: "full_report", type: "string" },
            { name: "report_subject", type: "string" },
          ],
        },
      }),

      // 8. Report distribution email
      emailNode(
        "8",
        "Distribute weekly report to leadership team",
        1720,
        "{leadership_distribution_list}",
        "{report_subject}",
        "{full_report}",
      ),

      // 9. Report archive Sheets
      sheetsNode(
        "9",
        "Archive weekly report metrics to spreadsheet",
        1960,
        "WeeklyReports",
        `[["{report_period}", "{health_score}", "{total_deals_this_week}", "{weekly_visits}", "{weekly_new_contacts}", "{weekly_new_leads}", "{weekly_bounce_rate}", "{weekly_tickets_created}", "{conversion_rate}", "{weekly_executive_summary}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1457. WEEKLY STATUS REPORT EMAIL (8 nodes)
  // ===================================================================

  "Weekly Status Report Email": {
    summary:
      "Runs Monday mornings to pull project data from Jira, aggregates team activity with runCode, AI-composes status narrative, routes by stakeholder type with runCode, sends executive status email, sends team status email, and logs report metrics to Sheets.",
    nodes: [
      trigger("Schedule: Monday morning status report generation", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs every Monday at 8 AM to compile the weekly project status report from Jira, aggregate team accomplishments, and distribute tailored summaries to executives and team members",
        setup:
          "Configure Jira API credentials with project access. Prepare distribution lists for executives and team. Set up Google Sheets for report tracking.",
        inputFields: [],
      }),

      // 2. Project tracker data pull from Jira
      n("2", "hitApi", "Fetch project sprint data from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search?jql=project={jira_project_key}%20AND%20updated%3E%3D-7d&fields=key,summary,status,assignee,priority,issuetype,resolutiondate&maxResults=100",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "total",
              customName: "total_issues_updated",
              type: "number",
            },
            { name: "issues", customName: "jira_issues_data", type: "string" },
          ],
        },
      }),

      // 3. Team activity aggregation
      n("3", "runCode", "Aggregate team activity metrics from Jira data", 520, {
        runCodeDetails: {
          code: `const issues = JSON.parse(variables.jira_issues_data || '[]');
let completed = 0, inProgress = 0, blocked = 0, newIssues = 0;
const byAssignee = {};
issues.forEach(issue => {
  const status = (issue.fields.status.name || '').toLowerCase();
  const assignee = issue.fields.assignee ? issue.fields.assignee.displayName : 'Unassigned';
  if (status === 'done' || status === 'resolved' || status === 'closed') completed++;
  else if (status === 'in progress' || status === 'in review') inProgress++;
  else if (status === 'blocked') blocked++;
  else newIssues++;
  if (!byAssignee[assignee]) byAssignee[assignee] = { completed: 0, active: 0 };
  if (status === 'done' || status === 'resolved') byAssignee[assignee].completed++;
  else byAssignee[assignee].active++;
});
output.completed_count = completed;
output.in_progress_count = inProgress;
output.blocked_count = blocked;
output.new_issues_count = newIssues;
output.team_breakdown = Object.entries(byAssignee).map(([name, stats]) => name + ': ' + stats.completed + ' done, ' + stats.active + ' active').join('; ');
output.completion_rate = issues.length > 0 ? ((completed / issues.length) * 100).toFixed(1) + '%' : '0%';
output.sprint_health = blocked > 3 ? 'At Risk' : completed > inProgress ? 'On Track' : 'Needs Attention';`,
          outputVariables: [
            { name: "completed_count", type: "number" },
            { name: "in_progress_count", type: "number" },
            { name: "blocked_count", type: "number" },
            { name: "new_issues_count", type: "number" },
            { name: "team_breakdown", type: "string" },
            { name: "completion_rate", type: "string" },
            { name: "sprint_health", type: "string" },
          ],
        },
      }),

      // 4. AI status composer
      aiNode(
        "4",
        "AI compose weekly status narrative",
        760,
        "You are a project manager writing a weekly status report. Based on the sprint metrics, write two versions: (1) an executive summary (3-4 sentences, focus on progress, risks, and blockers) and (2) a detailed team update (include specific accomplishments, what is in progress, and what needs attention). Be concrete with numbers. Return JSON: {executive_summary, team_detail, key_accomplishments, blockers_risks, next_week_priorities, status_emoji}",
        "Sprint Health: {sprint_health}\\nCompletion Rate: {completion_rate}\\nCompleted: {completed_count}, In Progress: {in_progress_count}, Blocked: {blocked_count}, New: {new_issues_count}\\nTotal Updated: {total_issues_updated}\\nTeam Breakdown: {team_breakdown}",
        [
          {
            field: "executive_summary",
            as: "exec_status_summary",
            type: "string",
          },
          { field: "team_detail", as: "team_status_detail", type: "string" },
          {
            field: "key_accomplishments",
            as: "key_accomplishments",
            type: "string",
          },
          { field: "blockers_risks", as: "blockers_and_risks", type: "string" },
          {
            field: "next_week_priorities",
            as: "next_week_priorities",
            type: "string",
          },
        ],
      ),

      // 5. Stakeholder routing
      n("5", "runCode", "Prepare stakeholder-specific email content", 1000, {
        runCodeDetails: {
          code: `output.exec_subject = 'Weekly Status: ' + variables.sprint_health + ' - ' + variables.completion_rate + ' Complete';
output.team_subject = 'Team Weekly Recap: ' + variables.completed_count + ' Items Done, ' + variables.blocked_count + ' Blocked';
output.exec_body = 'Weekly Project Status\\n\\n' + variables.exec_status_summary + '\\n\\nKey Accomplishments:\\n' + variables.key_accomplishments + '\\n\\nBlockers & Risks:\\n' + variables.blockers_and_risks + '\\n\\nNext Week Priorities:\\n' + variables.next_week_priorities;
output.team_body = 'Team Weekly Status Update\\n\\nSprint Health: ' + variables.sprint_health + ' (' + variables.completion_rate + ')\\n\\n' + variables.team_status_detail + '\\n\\nTeam Breakdown:\\n' + variables.team_breakdown + '\\n\\nKey Accomplishments:\\n' + variables.key_accomplishments + '\\n\\nBlockers:\\n' + variables.blockers_and_risks + '\\n\\nPriorities for Next Week:\\n' + variables.next_week_priorities;
output.report_date = new Date().toISOString().split('T')[0];`,
          outputVariables: [
            { name: "exec_subject", type: "string" },
            { name: "team_subject", type: "string" },
            { name: "exec_body", type: "string" },
            { name: "team_body", type: "string" },
            { name: "report_date", type: "string" },
          ],
        },
      }),

      // 6. Executive status email
      emailNode(
        "6",
        "Send executive status report email",
        1240,
        "{executive_distribution_list}",
        "{exec_subject}",
        "{exec_body}",
      ),

      // 7. Team status email
      emailNode(
        "7",
        "Send detailed team status report email",
        1480,
        "{team_distribution_list}",
        "{team_subject}",
        "{team_body}",
      ),

      // 8. Report metrics logger
      sheetsNode(
        "8",
        "Log weekly status report metrics",
        1720,
        "StatusReports",
        `[["{report_date}", "{sprint_health}", "{completion_rate}", "{completed_count}", "{in_progress_count}", "{blocked_count}", "{new_issues_count}", "{total_issues_updated}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1458. WEEKLY TEAM DIGEST EMAIL (8 nodes)
  // ===================================================================

  "Weekly Team Digest Email": {
    summary:
      "Runs Fridays to aggregate email threads with runCode, collect Slack highlights via API, AI-generates team digest, ranks by priority with runCode, builds digest email with runCode, sends digest delivery email, and tracks engagement in Sheets.",
    nodes: [
      trigger("Schedule: Friday team digest compilation", "schedule", {
        schedule: "Every Friday at 4:00 PM",
        howItStarts:
          "Runs every Friday at 4 PM to compile the week's most important communications, Slack highlights, and team updates into a curated digest email for the team",
        setup:
          "Configure Slack API token for channel history access. Set up email threading API or forwarding rules. Prepare digest distribution list. Set up engagement tracking spreadsheet.",
        inputFields: [],
      }),

      // 2. Email thread aggregator
      n(
        "2",
        "runCode",
        "Aggregate and summarize key email threads from the week",
        280,
        {
          runCodeDetails: {
            code: `const weekStart = new Date();
weekStart.setDate(weekStart.getDate() - 5);
output.week_start = weekStart.toISOString().split('T')[0];
output.week_end = new Date().toISOString().split('T')[0];
output.email_thread_query = 'after:' + output.week_start + ' is:important OR is:starred';
output.digest_period = output.week_start + ' to ' + output.week_end;
output.compilation_timestamp = new Date().toISOString();`,
            outputVariables: [
              { name: "week_start", type: "string" },
              { name: "week_end", type: "string" },
              { name: "email_thread_query", type: "string" },
              { name: "digest_period", type: "string" },
              { name: "compilation_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 3. Slack highlights collector
      n("3", "hitApi", "Collect Slack channel highlights from this week", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={slack_general_channel_id}&oldest={week_start_epoch}&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "messages.length",
              customName: "total_slack_messages",
              type: "number",
            },
            {
              name: "messages",
              customName: "slack_messages_data",
              type: "string",
            },
            {
              name: "has_more",
              customName: "has_more_messages",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. AI digest generator
      aiNode(
        "4",
        "AI generate curated weekly team digest",
        760,
        "You are an internal communications editor. From the Slack messages and activity data, create a curated weekly digest. Identify: top announcements, key decisions made, important discussions, upcoming deadlines mentioned, team wins/celebrations, and action items. Rank by importance. Return JSON: {digest_title, top_announcements, key_decisions, important_discussions, upcoming_deadlines, team_wins, action_items, quick_stats}",
        "Digest Period: {digest_period}\\nTotal Slack Messages: {total_slack_messages}\\nSlack Data: {slack_messages_data}",
        [
          { field: "digest_title", as: "digest_title", type: "string" },
          {
            field: "top_announcements",
            as: "top_announcements",
            type: "string",
          },
          { field: "key_decisions", as: "key_decisions", type: "string" },
          {
            field: "important_discussions",
            as: "important_discussions",
            type: "string",
          },
          {
            field: "upcoming_deadlines",
            as: "upcoming_deadlines",
            type: "string",
          },
          { field: "team_wins", as: "team_wins", type: "string" },
          { field: "action_items", as: "digest_action_items", type: "string" },
          { field: "quick_stats", as: "digest_quick_stats", type: "string" },
        ],
      ),

      // 5. Priority ranker
      n("5", "runCode", "Rank digest sections by priority and format", 1000, {
        runCodeDetails: {
          code: `const sections = [
  { priority: 1, title: 'Announcements', content: variables.top_announcements },
  { priority: 2, title: 'Key Decisions', content: variables.key_decisions },
  { priority: 3, title: 'Action Items', content: variables.digest_action_items },
  { priority: 4, title: 'Upcoming Deadlines', content: variables.upcoming_deadlines },
  { priority: 5, title: 'Team Wins', content: variables.team_wins },
  { priority: 6, title: 'Discussions', content: variables.important_discussions },
];
const ranked = sections.filter(s => s.content && s.content.trim().length > 5);
output.ranked_sections = ranked.map(s => '--- ' + s.title.toUpperCase() + ' ---\\n' + s.content).join('\\n\\n');
output.section_count = ranked.length;
output.has_action_items = (variables.digest_action_items || '').length > 5;`,
          outputVariables: [
            { name: "ranked_sections", type: "string" },
            { name: "section_count", type: "number" },
            { name: "has_action_items", type: "boolean" },
          ],
        },
      }),

      // 6. Digest email builder
      n("6", "runCode", "Build final digest email document", 1240, {
        runCodeDetails: {
          code: `output.digest_email_subject = variables.digest_title + ' - ' + variables.digest_period;
output.digest_email_body = '=== ' + variables.digest_title + ' ===\\n' +
  'Period: ' + variables.digest_period + '\\n' +
  'Quick Stats: ' + variables.digest_quick_stats + '\\n' +
  'Sections: ' + variables.section_count + '\\n\\n' +
  variables.ranked_sections + '\\n\\n' +
  '---\\nThis digest was compiled from ' + variables.total_slack_messages + ' Slack messages.\\nHave a great weekend!';`,
          outputVariables: [
            { name: "digest_email_subject", type: "string" },
            { name: "digest_email_body", type: "string" },
          ],
        },
      }),

      // 7. Digest delivery email
      emailNode(
        "7",
        "Send weekly team digest email",
        1480,
        "{team_distribution_list}",
        "{digest_email_subject}",
        "{digest_email_body}",
      ),

      // 8. Engagement tracker
      sheetsNode(
        "8",
        "Log digest compilation metrics to tracker",
        1720,
        "DigestTracker",
        `[["{compilation_timestamp}", "{digest_period}", "{total_slack_messages}", "{section_count}", "{has_action_items}", "{digest_title}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1459. WIN LOSS ANALYSIS BOT (8 nodes)
  // ===================================================================

  "Win Loss Analysis Bot": {
    summary:
      "Receives deal closed webhook, AI-extracts deal data, loads deal context from HubSpot, AI-conducts analysis interview, analyzes responses with runCode, tags competitive intelligence via condition, sends analysis report email, and enriches CRM with findings.",
    nodes: [
      trigger("Webhook: CRM deal closed (won or lost)", "webhook_app", {
        source: "hubspot",
        event: "deal.closed",
        howItStarts:
          "Fires when a deal is marked as closed-won or closed-lost in HubSpot, sending the raw deal closure payload with deal ID, outcome, amount, and associated contacts",
        setup:
          "Configure HubSpot to POST deal.closed webhooks to this URL. Set up CRM API access for deal context enrichment. Prepare competitive intelligence tracking spreadsheet.",
        inputFields: [],
      }),

      // 2. AI extract deal data
      aiNode(
        "2",
        "AI extract deal outcome data from webhook",
        280,
        "Extract deal closure data from this raw HubSpot webhook payload. Find the deal ID, deal name, deal amount, deal stage (closed-won or closed-lost), close date, deal owner name, deal owner email, associated company name, associated contact email, deal pipeline, and any notes or loss reason regardless of field naming format. Return JSON: {deal_id, deal_name, deal_amount, deal_outcome, close_date, owner_name, owner_email, company_name, contact_email, pipeline, loss_reason, deal_notes}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_outcome", as: "deal_outcome", type: "string" },
          { field: "close_date", as: "deal_close_date", type: "string" },
          { field: "owner_name", as: "deal_owner_name", type: "string" },
          { field: "owner_email", as: "deal_owner_email", type: "string" },
          { field: "company_name", as: "deal_company", type: "string" },
          { field: "contact_email", as: "deal_contact_email", type: "string" },
          { field: "pipeline", as: "deal_pipeline", type: "string" },
          { field: "loss_reason", as: "deal_loss_reason", type: "string" },
        ],
      ),

      // 3. Deal context loader HubSpot
      hubspotGetNode(
        "3",
        "Load full deal context and history from HubSpot",
        520,
        "crm/v3/objects/deals/{deal_id}?properties=dealname,amount,dealstage,closedate,notes_last_updated,num_contacted_notes,num_notes,hs_analytics_source,hs_deal_stage_probability,days_to_close",
        [
          {
            name: "properties.hs_analytics_source",
            customName: "deal_source",
            type: "string",
          },
          {
            name: "properties.days_to_close",
            customName: "deal_cycle_days",
            type: "string",
          },
          {
            name: "properties.num_notes",
            customName: "deal_notes_count",
            type: "string",
          },
          {
            name: "properties.hs_deal_stage_probability",
            customName: "final_probability",
            type: "string",
          },
        ],
      ),

      // 4. AI analysis conductor
      aiNode(
        "4",
        "AI conduct win-loss analysis of deal outcome",
        760,
        "You are a sales strategy analyst conducting a win/loss analysis. Analyze this deal outcome thoroughly. Identify: (1) primary factors for the win or loss, (2) competitive dynamics, (3) sales process effectiveness, (4) pricing/value perception, (5) decision-maker engagement, (6) timing factors. If it was a loss, provide specific improvement recommendations. If a win, identify replicable patterns. Return JSON: {analysis_summary, primary_factors, competitor_mentioned, competitor_name, sales_process_score, pricing_factor, decision_maker_engagement, timing_assessment, key_learnings, recommendations, analysis_confidence}",
        "Deal: {deal_name}\\nOutcome: {deal_outcome}\\nAmount: ${deal_amount}\\nCompany: {deal_company}\\nPipeline: {deal_pipeline}\\nDeal Source: {deal_source}\\nCycle Days: {deal_cycle_days}\\nNotes Count: {deal_notes_count}\\nLoss Reason: {deal_loss_reason}\\nFinal Probability: {final_probability}\\nOwner: {deal_owner_name}",
        [
          { field: "analysis_summary", as: "analysis_summary", type: "string" },
          { field: "primary_factors", as: "primary_factors", type: "string" },
          {
            field: "competitor_mentioned",
            as: "competitor_mentioned",
            type: "string",
          },
          { field: "competitor_name", as: "competitor_name", type: "string" },
          { field: "sales_process_score", as: "process_score", type: "string" },
          { field: "key_learnings", as: "key_learnings", type: "string" },
          {
            field: "recommendations",
            as: "analysis_recommendations",
            type: "string",
          },
        ],
      ),

      // 5. Response analyzer
      n("5", "runCode", "Compute analysis scores and tag categories", 1000, {
        runCodeDetails: {
          code: `const outcome = variables.deal_outcome || '';
const amount = parseFloat(variables.deal_amount) || 0;
const cycleDays = parseInt(variables.deal_cycle_days) || 0;
const competitorMentioned = variables.competitor_mentioned === 'true';
output.is_high_value = amount > 50000;
output.is_long_cycle = cycleDays > 90;
output.has_competitor_intel = competitorMentioned;
output.deal_category = outcome.toLowerCase().includes('won') ? 'WIN' : 'LOSS';
output.analysis_tags = [
  output.deal_category,
  output.is_high_value ? 'HIGH_VALUE' : 'STANDARD',
  competitorMentioned ? 'COMPETITIVE' : 'NO_COMPETITOR',
  output.is_long_cycle ? 'LONG_CYCLE' : 'NORMAL_CYCLE'
].join(', ');
output.report_title = output.deal_category + ' Analysis: ' + variables.deal_name + ' ($' + amount.toLocaleString() + ')';`,
          outputVariables: [
            { name: "is_high_value", type: "boolean" },
            { name: "is_long_cycle", type: "boolean" },
            { name: "has_competitor_intel", type: "boolean" },
            { name: "deal_category", type: "string" },
            { name: "analysis_tags", type: "string" },
            { name: "report_title", type: "string" },
          ],
        },
      }),

      // 6. Competitive intelligence tagger condition
      n(
        "6",
        "condition",
        "Check if competitive intelligence was detected",
        1240,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "has_competitor_intel",
                operator: "equals",
                value: "true",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 7. Analysis report email (True path - with competitor intel)
      emailNode(
        "7",
        "Send win-loss analysis report with competitor intel",
        1480,
        "{deal_owner_email}",
        "{report_title} [Competitive Intel: {competitor_name}]",
        "Win/Loss Analysis Report\\n\\n{report_title}\\nTags: {analysis_tags}\\n\\nDeal Details:\\n- Company: {deal_company}\\n- Amount: ${deal_amount}\\n- Cycle: {deal_cycle_days} days\\n- Source: {deal_source}\\n- Pipeline: {deal_pipeline}\\n\\nAnalysis Summary:\\n{analysis_summary}\\n\\nPrimary Factors:\\n{primary_factors}\\n\\nCompetitor Intelligence:\\n- Competitor: {competitor_name}\\n\\nKey Learnings:\\n{key_learnings}\\n\\nRecommendations:\\n{analysis_recommendations}\\n\\nProcess Score: {process_score}\\n\\n- Sales Intelligence Automation",
      ),

      // 8. CRM enrichment HubSpot (both paths)
      hubspotPostNode(
        "8",
        "Enrich HubSpot deal record with analysis findings",
        1480,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"win_loss_analysis":"{analysis_summary}","win_loss_tags":"{analysis_tags}","competitor_identified":"{competitor_name}","process_score":"{process_score}","key_learnings":"{key_learnings}"}',
          },
        ],
        [{ name: "id", customName: "enriched_deal_id", type: "string" }],
        400,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1460. WORK REQUEST INTAKE BOT (8 nodes)
  // ===================================================================

  "Work Request Intake Bot": {
    summary:
      "Receives work request webhook, AI-extracts request data, AI-classifies request type and priority, verifies requester identity with runCode, estimates priority and effort with runCode, creates PM ticket in Asana, confirms receipt via Slack, and sends team notification email.",
    nodes: [
      trigger("Webhook: New work request submitted", "webhook_app", {
        source: "request_portal",
        event: "request.submitted",
        howItStarts:
          "Fires when a team member submits a work request through the internal portal, Slack, or email, sending the raw request payload with project details, requester info, and requirements",
        setup:
          "Configure internal request portal to POST requests to this URL. Set up Asana API for project management. Configure Slack webhook for team channel. Prepare requester verification logic.",
        inputFields: [],
      }),

      // 2. AI extract request data
      aiNode(
        "2",
        "AI extract work request details from webhook",
        280,
        "Extract work request data from this raw webhook payload. Find the requester name, requester email, requester department, request title, request description, desired deadline, project or team the request is for, any attached files, and urgency indicators regardless of field naming format. Return JSON: {requester_name, requester_email, requester_department, request_title, request_description, desired_deadline, target_team, attachments, urgency_indicator}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          {
            field: "requester_department",
            as: "requester_department",
            type: "string",
          },
          { field: "request_title", as: "request_title", type: "string" },
          {
            field: "request_description",
            as: "request_description",
            type: "string",
          },
          { field: "desired_deadline", as: "desired_deadline", type: "string" },
          { field: "target_team", as: "target_team", type: "string" },
          { field: "attachments", as: "request_attachments", type: "string" },
          { field: "urgency_indicator", as: "stated_urgency", type: "string" },
        ],
      ),

      // 3. AI request classifier
      aiNode(
        "3",
        "AI classify work request type and complexity",
        520,
        "You are a project intake coordinator. Classify this work request by: (1) category (bug fix, feature request, content update, design task, infrastructure, research, operational), (2) estimated complexity (small, medium, large, epic), (3) suggested priority (P0-critical, P1-high, P2-medium, P3-low), (4) estimated effort in hours. Consider the description, stated urgency, and typical patterns. Return JSON: {category, complexity, suggested_priority, estimated_hours, required_skills, acceptance_criteria, clarification_needed, classification_confidence}",
        "Title: {request_title}\\nDescription: {request_description}\\nRequester: {requester_name} ({requester_department})\\nTarget Team: {target_team}\\nDesired Deadline: {desired_deadline}\\nUrgency: {stated_urgency}\\nAttachments: {request_attachments}",
        [
          { field: "category", as: "request_category", type: "string" },
          { field: "complexity", as: "request_complexity", type: "string" },
          {
            field: "suggested_priority",
            as: "suggested_priority",
            type: "string",
          },
          { field: "estimated_hours", as: "estimated_hours", type: "string" },
          { field: "required_skills", as: "required_skills", type: "string" },
          {
            field: "acceptance_criteria",
            as: "acceptance_criteria",
            type: "string",
          },
          {
            field: "clarification_needed",
            as: "needs_clarification",
            type: "string",
          },
        ],
      ),

      // 4. Requester verification
      n("4", "runCode", "Verify requester identity and authorization", 760, {
        runCodeDetails: {
          code: `const email = variables.requester_email || '';
const department = variables.requester_department || '';
const isInternalEmail = email.endsWith('@company.com') || email.endsWith('@omniengage.com');
const authorizedDepartments = ['engineering', 'product', 'marketing', 'sales', 'operations', 'design', 'finance', 'hr'];
const isAuthorized = isInternalEmail && authorizedDepartments.includes(department.toLowerCase());
output.requester_verified = isAuthorized;
output.verification_status = isAuthorized ? 'VERIFIED' : 'UNVERIFIED';
output.requester_risk = isInternalEmail ? 'low' : 'high';
output.processed_at = new Date().toISOString();`,
          outputVariables: [
            { name: "requester_verified", type: "boolean" },
            { name: "verification_status", type: "string" },
            { name: "requester_risk", type: "string" },
            { name: "processed_at", type: "string" },
          ],
        },
      }),

      // 5. Priority and effort estimator
      n(
        "5",
        "runCode",
        "Calculate final priority score and effort estimate",
        1000,
        {
          runCodeDetails: {
            code: `const priorityMap = { 'P0-critical': 4, 'P1-high': 3, 'P2-medium': 2, 'P3-low': 1 };
const complexityMap = { 'small': 1, 'medium': 2, 'large': 3, 'epic': 4 };
const priority = variables.suggested_priority || 'P2-medium';
const complexity = variables.request_complexity || 'medium';
const hours = parseFloat(variables.estimated_hours) || 8;
const priorityScore = priorityMap[priority] || 2;
const complexityScore = complexityMap[complexity] || 2;
const urgencyScore = priorityScore * 10 + (5 - complexityScore);
output.final_priority = priority;
output.urgency_score = urgencyScore;
output.effort_label = hours <= 4 ? 'Quick Win' : hours <= 16 ? 'Sprint Task' : hours <= 40 ? 'Multi-Sprint' : 'Epic';
output.suggested_sprint = hours <= 16 ? 'Current Sprint' : 'Next Sprint';
output.task_summary = '[' + priority + '] ' + variables.request_title + ' (' + variables.request_category + ', ~' + hours + 'h, ' + output.effort_label + ')';`,
            outputVariables: [
              { name: "final_priority", type: "string" },
              { name: "urgency_score", type: "number" },
              { name: "effort_label", type: "string" },
              { name: "suggested_sprint", type: "string" },
              { name: "task_summary", type: "string" },
            ],
          },
        },
      ),

      // 6. Asana ticket creation
      n("6", "hitApi", "Create project task in Asana", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"{task_summary}","notes":"Request from: {requester_name} ({requester_department})\\nEmail: {requester_email}\\nCategory: {request_category}\\nComplexity: {request_complexity}\\nEstimated Hours: {estimated_hours}\\nRequired Skills: {required_skills}\\n\\nDescription:\\n{request_description}\\n\\nAcceptance Criteria:\\n{acceptance_criteria}\\n\\nClarification Needed: {needs_clarification}","projects":["{asana_project_id}"],"due_on":"{desired_deadline}","tags":["{request_category}","{final_priority}"]}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
            {
              name: "data.name",
              customName: "asana_task_name",
              type: "string",
            },
          ],
        },
      }),

      // 7. Confirmation Slack
      slackNode(
        "7",
        "Confirm work request receipt in Slack",
        1480,
        ":inbox_tray: *New Work Request Created*\\n>*{task_summary}*\\n>Requester: {requester_name} ({requester_department})\\n>Priority: {final_priority} | Effort: {effort_label} ({estimated_hours}h)\\n>Sprint: {suggested_sprint}\\n>Skills: {required_skills}\\n>Asana: <{asana_task_url}|{asana_task_id}>\\n>Verification: {verification_status}",
      ),

      // 8. Team notification email
      emailNode(
        "8",
        "Send team notification email for new work request",
        1720,
        "{target_team_email}",
        "New Work Request: {task_summary}",
        "A new work request has been submitted and triaged:\\n\\nRequest: {request_title}\\nFrom: {requester_name} ({requester_department})\\nCategory: {request_category}\\nPriority: {final_priority}\\nComplexity: {request_complexity}\\nEstimated Effort: {estimated_hours} hours ({effort_label})\\nSuggested Sprint: {suggested_sprint}\\n\\nDescription:\\n{request_description}\\n\\nAcceptance Criteria:\\n{acceptance_criteria}\\n\\nNeeds Clarification: {needs_clarification}\\n\\nAsana Task: {asana_task_url}\\n\\n- Work Request Intake Bot",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1461. WORKFLOW ERROR HANDLER (9 nodes)
  // ===================================================================

  "Workflow Error Handler": {
    summary:
      "Receives error event webhook, AI-extracts error data, classifies error type with runCode, routes transient errors for retry via condition, handles rate limits with runCode, routes permanent errors via condition, logs to dead letter queue Sheets, dispatches critical Slack alert, and validates auto-recovery.",
    nodes: [
      trigger("Webhook: Workflow error event received", "webhook_app", {
        source: "workflow_engine",
        event: "workflow.error",
        howItStarts:
          "Fires when a workflow execution encounters an error, sending the raw error event payload with workflow ID, step ID, error message, stack trace, and execution context",
        setup:
          "Configure workflow engine to POST error events to this URL. Set up retry mechanism with exponential backoff. Prepare dead letter queue spreadsheet. Configure Slack webhook for critical alerts.",
        inputFields: [],
      }),

      // 2. AI extract error data
      aiNode(
        "2",
        "AI extract error details from workflow event",
        280,
        "Extract workflow error data from this raw webhook payload. Find the workflow ID, workflow name, failed step ID, failed step name, error type, error message, error code, stack trace, execution ID, retry count, timestamp, and any context data regardless of field naming format. Return JSON: {workflow_id, workflow_name, step_id, step_name, error_type, error_message, error_code, stack_trace, execution_id, retry_count, error_timestamp, context_data}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "workflow_id", as: "workflow_id", type: "string" },
          { field: "workflow_name", as: "workflow_name", type: "string" },
          { field: "step_id", as: "failed_step_id", type: "string" },
          { field: "step_name", as: "failed_step_name", type: "string" },
          { field: "error_type", as: "error_type", type: "string" },
          { field: "error_message", as: "error_message", type: "string" },
          { field: "error_code", as: "error_code", type: "string" },
          { field: "stack_trace", as: "error_stack", type: "string" },
          { field: "execution_id", as: "execution_id", type: "string" },
          { field: "retry_count", as: "current_retry_count", type: "string" },
          { field: "error_timestamp", as: "error_timestamp", type: "string" },
        ],
      ),

      // 3. Error classifier
      n(
        "3",
        "runCode",
        "Classify error as transient, rate-limited, or permanent",
        520,
        {
          runCodeDetails: {
            code: `const errorType = (variables.error_type || '').toLowerCase();
const errorCode = variables.error_code || '';
const errorMsg = (variables.error_message || '').toLowerCase();
const retryCount = parseInt(variables.current_retry_count) || 0;
const maxRetries = 3;
const transientPatterns = ['timeout', 'econnreset', 'econnrefused', 'socket hang up', 'network', '503', '502', '504'];
const rateLimitPatterns = ['rate limit', 'too many requests', '429', 'throttle', 'quota exceeded'];
const isTransient = transientPatterns.some(p => errorMsg.includes(p) || errorCode.includes(p) || errorType.includes(p));
const isRateLimit = rateLimitPatterns.some(p => errorMsg.includes(p) || errorCode.includes(p));
const canRetry = retryCount < maxRetries;
output.error_category = isRateLimit ? 'RATE_LIMITED' : isTransient ? 'TRANSIENT' : 'PERMANENT';
output.is_retryable = (isTransient || isRateLimit) && canRetry;
output.is_rate_limited = isRateLimit;
output.retry_delay_seconds = isRateLimit ? 60 : isTransient ? Math.pow(2, retryCount) * 5 : 0;
output.next_retry_count = retryCount + 1;
output.severity = (!canRetry || !isTransient && !isRateLimit) ? 'CRITICAL' : 'WARNING';`,
            outputVariables: [
              { name: "error_category", type: "string" },
              { name: "is_retryable", type: "boolean" },
              { name: "is_rate_limited", type: "boolean" },
              { name: "retry_delay_seconds", type: "number" },
              { name: "next_retry_count", type: "number" },
              { name: "severity", type: "string" },
            ],
          },
        },
      ),

      // 4. Transient error retry condition
      n(
        "4",
        "condition",
        "Check if error is retryable (transient or rate-limited)",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "is_retryable", operator: "equals", value: "true" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Rate limit handler (True path)
      n(
        "5",
        "runCode",
        "Calculate backoff delay and prepare retry payload",
        1000,
        {
          runCodeDetails: {
            code: `const delaySeconds = parseInt(variables.retry_delay_seconds) || 10;
const isRateLimit = variables.is_rate_limited === 'true';
output.retry_payload = JSON.stringify({
  workflow_id: variables.workflow_id,
  execution_id: variables.execution_id,
  step_id: variables.failed_step_id,
  retry_count: variables.next_retry_count,
  delay_seconds: delaySeconds,
  error_category: variables.error_category,
  original_error: variables.error_message
});
output.retry_message = 'Scheduling retry #' + variables.next_retry_count + ' for workflow ' + variables.workflow_name + ' step ' + variables.failed_step_name + ' in ' + delaySeconds + 's. Error: ' + variables.error_category;
output.backoff_strategy = isRateLimit ? 'Fixed 60s rate-limit cooldown' : 'Exponential backoff: ' + delaySeconds + 's';`,
            outputVariables: [
              { name: "retry_payload", type: "string" },
              { name: "retry_message", type: "string" },
              { name: "backoff_strategy", type: "string" },
            ],
          },
        },
      ),

      // 6. Permanent error router condition (False path from node 4)
      n(
        "6",
        "condition",
        "Check if permanent error is critical severity",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "severity", operator: "equals", value: "CRITICAL" },
            ],
            logicalOperator: "AND",
          },
        },
        400,
      ),

      // 7. Dead letter queue
      sheetsNode(
        "7",
        "Log failed execution to dead letter queue",
        1240,
        "DeadLetterQueue",
        `[["{error_timestamp}", "{workflow_id}", "{workflow_name}", "{failed_step_id}", "{failed_step_name}", "{error_category}", "{error_code}", "{error_message}", "{current_retry_count}", "{severity}", "{execution_id}"]]`,
      ),

      // 8. Critical alert dispatcher Slack
      slackNode(
        "8",
        "Dispatch critical error alert to Slack ops channel",
        1480,
        ":rotating_light: *CRITICAL WORKFLOW ERROR*\\n>*Workflow:* {workflow_name} ({workflow_id})\\n>*Failed Step:* {failed_step_name} ({failed_step_id})\\n>*Error:* {error_category} - {error_message}\\n>*Code:* {error_code}\\n>*Execution:* {execution_id}\\n>*Retries:* {current_retry_count}\\n>*Severity:* {severity}\\n>*Timestamp:* {error_timestamp}\\n>\\n>Immediate investigation required. Check dead letter queue for full context.",
      ),

      // 9. Auto-recovery validator
      n(
        "9",
        "runCode",
        "Validate auto-recovery readiness and log outcome",
        1720,
        {
          runCodeDetails: {
            code: `const category = variables.error_category || '';
const retryCount = parseInt(variables.current_retry_count) || 0;
const severity = variables.severity || '';
output.recovery_action = category === 'RATE_LIMITED' ? 'WAIT_AND_RETRY' : category === 'TRANSIENT' ? 'AUTO_RETRY' : 'MANUAL_INTERVENTION';
output.recovery_status = severity === 'CRITICAL' ? 'NEEDS_HUMAN' : 'AUTO_HANDLED';
output.incident_id = 'INC-' + Date.now().toString(36).toUpperCase();
output.resolution_summary = 'Error ' + variables.error_code + ' in ' + variables.workflow_name + ' at step ' + variables.failed_step_name + '. Category: ' + category + '. Recovery: ' + output.recovery_action + '. Incident: ' + output.incident_id;`,
            outputVariables: [
              { name: "recovery_action", type: "string" },
              { name: "recovery_status", type: "string" },
              { name: "incident_id", type: "string" },
              { name: "resolution_summary", type: "string" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1462. WORKPLACE INCIDENT REPORTING BOT (9 nodes)
  // ===================================================================

  "Workplace Incident Reporting Bot": {
    summary:
      "Receives incident report webhook, AI-conducts incident interview extraction, classifies severity via condition, processes photo/document evidence with runCode, creates incident record in Sheets, runs OSHA compliance check with runCode, sends immediate safety alert via Slack, notifies management via email, and schedules follow-up with delay.",
    nodes: [
      trigger("Webhook: Workplace incident report submitted", "webhook_app", {
        source: "safety_portal",
        event: "incident.reported",
        howItStarts:
          "Fires when an employee submits a workplace incident report through the safety portal or mobile app, sending the raw report payload with incident details, location, injuries, witnesses, and uploaded photos",
        setup:
          "Configure safety reporting portal to POST incidents to this URL. Set up OSHA compliance reference data. Configure Slack webhook for safety team channel. Prepare incident tracking spreadsheet.",
        inputFields: [],
      }),

      // 2. AI incident interviewer
      aiNode(
        "2",
        "AI extract and classify incident report details",
        280,
        "Extract workplace incident data from this raw report payload. Find the reporter name, reporter employee ID, reporter email, incident date and time, incident location (building, floor, area), incident type (slip/fall, equipment malfunction, chemical exposure, ergonomic, vehicle, fire, other), description of what happened, injuries sustained, first aid administered, witnesses present, photos or documents attached, and immediate corrective actions taken regardless of field naming format. Return JSON: {reporter_name, reporter_id, reporter_email, incident_datetime, location_building, location_area, incident_type, description, injuries, first_aid_given, witnesses, photo_urls, corrective_actions, severity_estimate}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "reporter_name", as: "reporter_name", type: "string" },
          { field: "reporter_id", as: "reporter_employee_id", type: "string" },
          { field: "reporter_email", as: "reporter_email", type: "string" },
          {
            field: "incident_datetime",
            as: "incident_datetime",
            type: "string",
          },
          {
            field: "location_building",
            as: "incident_building",
            type: "string",
          },
          { field: "location_area", as: "incident_area", type: "string" },
          { field: "incident_type", as: "incident_type", type: "string" },
          { field: "description", as: "incident_description", type: "string" },
          { field: "injuries", as: "injuries_reported", type: "string" },
          { field: "first_aid_given", as: "first_aid_given", type: "string" },
          { field: "witnesses", as: "witness_list", type: "string" },
          { field: "photo_urls", as: "incident_photos", type: "string" },
          {
            field: "corrective_actions",
            as: "immediate_actions",
            type: "string",
          },
          {
            field: "severity_estimate",
            as: "ai_severity_estimate",
            type: "string",
          },
        ],
      ),

      // 3. Severity classifier condition
      n(
        "3",
        "condition",
        "Check if incident severity requires immediate escalation",
        520,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "ai_severity_estimate",
                operator: "not_equals",
                value: "low",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 4. Photo document handler (True path - medium/high severity)
      n(
        "4",
        "runCode",
        "Process incident photos and generate evidence reference",
        760,
        {
          runCodeDetails: {
            code: `const photos = variables.incident_photos || '';
const photoList = photos ? photos.split(',').map(p => p.trim()).filter(Boolean) : [];
output.photo_count = photoList.length;
output.evidence_catalog = photoList.map((url, i) => 'Evidence ' + (i + 1) + ': ' + url).join('; ');
output.has_photos = photoList.length > 0;
const incidentId = 'WI-' + new Date().toISOString().replace(/[-:T.Z]/g, '').substring(0, 14);
output.incident_record_id = incidentId;
output.incident_severity = variables.ai_severity_estimate || 'medium';
output.requires_osha_report = variables.injuries_reported && variables.injuries_reported.toLowerCase() !== 'none' && variables.injuries_reported.toLowerCase() !== 'no';
output.investigation_deadline = new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().split('T')[0];`,
            outputVariables: [
              { name: "photo_count", type: "number" },
              { name: "evidence_catalog", type: "string" },
              { name: "has_photos", type: "boolean" },
              { name: "incident_record_id", type: "string" },
              { name: "incident_severity", type: "string" },
              { name: "requires_osha_report", type: "boolean" },
              { name: "investigation_deadline", type: "string" },
            ],
          },
        },
      ),

      // 5. Incident record creator Sheets
      sheetsNode(
        "5",
        "Create incident record in safety tracking spreadsheet",
        1000,
        "IncidentLog",
        `[["{incident_record_id}", "{incident_datetime}", "{reporter_name}", "{reporter_employee_id}", "{incident_building}", "{incident_area}", "{incident_type}", "{incident_severity}", "{injuries_reported}", "{first_aid_given}", "{witness_list}", "{photo_count}", "{incident_description}", "{immediate_actions}", "{requires_osha_report}", "{investigation_deadline}"]]`,
      ),

      // 6. OSHA compliance check
      n(
        "6",
        "runCode",
        "Check OSHA reporting requirements and compliance",
        1240,
        {
          runCodeDetails: {
            code: `const incidentType = (variables.incident_type || '').toLowerCase();
const injuries = (variables.injuries_reported || '').toLowerCase();
const requiresOsha = variables.requires_osha_report === 'true';
const oshaReportable = ['fatality', 'hospitalization', 'amputation', 'loss of eye'].some(t => injuries.includes(t));
const oshaRecordable = requiresOsha && !['first aid only', 'minor scratch', 'none'].some(t => injuries.includes(t));
output.osha_reportable = oshaReportable;
output.osha_recordable = oshaRecordable;
output.osha_deadline = oshaReportable ? '8 hours' : oshaRecordable ? '7 days' : 'N/A';
output.osha_form = oshaReportable ? 'OSHA Form 301 + immediate phone report to OSHA' : oshaRecordable ? 'OSHA Form 300 Log entry' : 'Internal record only';
output.compliance_notes = 'Incident: ' + variables.incident_record_id + '. Type: ' + variables.incident_type + '. OSHA Reportable: ' + oshaReportable + '. Recordable: ' + oshaRecordable + '. Deadline: ' + output.osha_deadline + '. Required form: ' + output.osha_form;`,
            outputVariables: [
              { name: "osha_reportable", type: "boolean" },
              { name: "osha_recordable", type: "boolean" },
              { name: "osha_deadline", type: "string" },
              { name: "osha_form", type: "string" },
              { name: "compliance_notes", type: "string" },
            ],
          },
        },
      ),

      // 7. Immediate safety alert Slack
      slackNode(
        "7",
        "Send immediate safety alert to Slack safety channel",
        1480,
        ":warning: *WORKPLACE INCIDENT REPORTED*\\n>*ID:* {incident_record_id}\\n>*Severity:* {incident_severity}\\n>*Type:* {incident_type}\\n>*Location:* {incident_building} - {incident_area}\\n>*Date/Time:* {incident_datetime}\\n>*Reporter:* {reporter_name} ({reporter_employee_id})\\n>*Injuries:* {injuries_reported}\\n>*First Aid:* {first_aid_given}\\n>*Witnesses:* {witness_list}\\n>*Photos:* {photo_count} attached\\n>*OSHA:* {osha_form} (Deadline: {osha_deadline})\\n>*Investigation Due:* {investigation_deadline}",
      ),

      // 8. Management notification email
      emailNode(
        "8",
        "Send incident notification to management and safety team",
        1720,
        "{safety_manager_email}",
        "Workplace Incident {incident_record_id}: {incident_type} at {incident_building} - {incident_severity} Severity",
        "Workplace Incident Report\\n\\nIncident ID: {incident_record_id}\\nDate/Time: {incident_datetime}\\nSeverity: {incident_severity}\\n\\nLocation: {incident_building}, {incident_area}\\nType: {incident_type}\\n\\nDescription:\\n{incident_description}\\n\\nInjuries: {injuries_reported}\\nFirst Aid Given: {first_aid_given}\\nImmediate Actions: {immediate_actions}\\n\\nReported By: {reporter_name} ({reporter_employee_id}, {reporter_email})\\nWitnesses: {witness_list}\\nEvidence: {evidence_catalog}\\n\\nOSHA Compliance:\\n{compliance_notes}\\n\\nInvestigation Deadline: {investigation_deadline}\\n\\nPlease begin your investigation immediately.\\n\\n- Safety Incident Automation System",
      ),

      // 9. Follow-up scheduler delay (also handles low-severity False path)
      n(
        "9",
        "delayAction",
        "Schedule 48-hour follow-up for incident investigation",
        1960,
        {
          delayDetails: { duration: 48, unit: "hours" },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "9", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1463. BOOK SUMMARY BOT (7 nodes)
  // ===================================================================

  "Book Summary Bot": {
    summary:
      "Receives book request webhook, AI-extracts book data, AI-identifies the book via knowledge base, AI-generates comprehensive summary, adapts reading level with runCode, AI-recommends related books, and delivers summary via email.",
    nodes: [
      trigger("Webhook: Book summary request received", "webhook_app", {
        source: "book_bot",
        event: "summary.requested",
        howItStarts:
          "Fires when a user requests a book summary through chat, email, or the book bot interface, sending the raw request payload with book title, author, and user preferences",
        setup:
          "Configure book bot interface to POST requests to this URL. Set up OpenAI API for book analysis. Configure email delivery for summary distribution.",
        inputFields: [],
      }),

      // 2. AI extract book data
      aiNode(
        "2",
        "AI extract book title and user preferences from request",
        280,
        "Extract book summary request data from this raw webhook payload. Find the user name, user email, requested book title, author name (if provided), preferred summary length (brief, standard, detailed), reading level preference (casual, professional, academic), specific topics of interest, and any format preferences regardless of field naming format. Return JSON: {user_name, user_email, book_title, author_name, summary_length, reading_level, topics_of_interest, format_preference}",
        "Raw webhook payload: {webhook_payload}",
        [
          { field: "user_name", as: "reader_name", type: "string" },
          { field: "user_email", as: "reader_email", type: "string" },
          { field: "book_title", as: "book_title", type: "string" },
          { field: "author_name", as: "book_author", type: "string" },
          { field: "summary_length", as: "preferred_length", type: "string" },
          { field: "reading_level", as: "reading_level", type: "string" },
          {
            field: "topics_of_interest",
            as: "reader_interests",
            type: "string",
          },
          { field: "format_preference", as: "format_pref", type: "string" },
        ],
      ),

      // 3. AI book identification
      aiNode(
        "3",
        "AI identify and verify book details",
        520,
        "You are a literary expert. Identify this book and verify details. Provide the full title, author, publication year, genre, page count estimate, and a one-sentence description. If the title is ambiguous, pick the most well-known version. Return JSON: {verified_title, verified_author, publication_year, genre, page_count, one_liner, isbn, is_fiction}",
        "Requested book: {book_title} by {book_author}",
        [
          { field: "verified_title", as: "verified_title", type: "string" },
          { field: "verified_author", as: "verified_author", type: "string" },
          { field: "publication_year", as: "pub_year", type: "string" },
          { field: "genre", as: "book_genre", type: "string" },
          { field: "page_count", as: "page_count", type: "string" },
          { field: "one_liner", as: "book_one_liner", type: "string" },
          { field: "is_fiction", as: "is_fiction", type: "string" },
        ],
      ),

      // 4. AI summary generator
      aiNode(
        "4",
        "AI generate comprehensive book summary",
        760,
        "You are a professional book summarizer. Create a comprehensive summary of this book. Include: (1) Overview - what the book is about and why it matters, (2) Key Themes - the 3-5 main ideas or themes, (3) Chapter/Section Breakdown - key points from each major section, (4) Notable Quotes - 2-3 memorable quotes or passages, (5) Practical Takeaways - actionable insights the reader can apply, (6) Strengths and Weaknesses - balanced assessment. Tailor the depth to the requested length. Return JSON: {overview, key_themes, chapter_breakdown, notable_quotes, practical_takeaways, strengths_weaknesses, overall_rating, who_should_read}",
        "Book: {verified_title} by {verified_author} ({pub_year})\\nGenre: {book_genre}\\nPages: {page_count}\\nSummary: {book_one_liner}\\nFiction: {is_fiction}\\nRequested Length: {preferred_length}\\nReader Interests: {reader_interests}",
        [
          { field: "overview", as: "book_overview", type: "string" },
          { field: "key_themes", as: "book_key_themes", type: "string" },
          { field: "chapter_breakdown", as: "chapter_summary", type: "string" },
          { field: "notable_quotes", as: "book_quotes", type: "string" },
          {
            field: "practical_takeaways",
            as: "book_takeaways",
            type: "string",
          },
          {
            field: "strengths_weaknesses",
            as: "book_assessment",
            type: "string",
          },
          { field: "overall_rating", as: "book_rating", type: "string" },
          { field: "who_should_read", as: "target_audience", type: "string" },
        ],
      ),

      // 5. Reading level adapter
      n(
        "5",
        "runCode",
        "Adapt summary content to reader's preferred level",
        1000,
        {
          runCodeDetails: {
            code: `const level = (variables.reading_level || 'standard').toLowerCase();
const format = (variables.format_pref || 'email').toLowerCase();
let intro = '';
if (level === 'casual') intro = 'Here is a friendly, easy-to-digest summary of';
else if (level === 'academic') intro = 'The following is a scholarly analysis of';
else intro = 'Here is your professional summary of';
output.formatted_summary = intro + ' "' + variables.verified_title + '" by ' + variables.verified_author + ' (' + variables.pub_year + ')\\n\\n' +
  'Rating: ' + variables.book_rating + ' | Genre: ' + variables.book_genre + ' | Pages: ' + variables.page_count + '\\n\\n' +
  '--- OVERVIEW ---\\n' + variables.book_overview + '\\n\\n' +
  '--- KEY THEMES ---\\n' + variables.book_key_themes + '\\n\\n' +
  '--- CHAPTER BREAKDOWN ---\\n' + variables.chapter_summary + '\\n\\n' +
  '--- NOTABLE QUOTES ---\\n' + variables.book_quotes + '\\n\\n' +
  '--- PRACTICAL TAKEAWAYS ---\\n' + variables.book_takeaways + '\\n\\n' +
  '--- ASSESSMENT ---\\n' + variables.book_assessment + '\\n\\n' +
  'Who should read this: ' + variables.target_audience;
output.summary_word_count = output.formatted_summary.split(/\\s+/).length;`,
            outputVariables: [
              { name: "formatted_summary", type: "string" },
              { name: "summary_word_count", type: "number" },
            ],
          },
        },
      ),

      // 6. Related books recommender AI
      aiNode(
        "6",
        "AI recommend related books based on reader interests",
        1240,
        "You are a book recommendation engine. Based on the book just summarized and the reader's interests, recommend 5 related books. For each, provide title, author, year, and a one-sentence reason why the reader would enjoy it. Return JSON: {recommendations, recommendation_count, reading_path}",
        "Just summarized: {verified_title} by {verified_author} ({book_genre})\\nReader interests: {reader_interests}\\nReading level: {reading_level}\\nKey themes enjoyed: {book_key_themes}",
        [
          {
            field: "recommendations",
            as: "book_recommendations",
            type: "string",
          },
          {
            field: "reading_path",
            as: "suggested_reading_path",
            type: "string",
          },
        ],
      ),

      // 7. Summary delivery email
      emailNode(
        "7",
        "Deliver book summary and recommendations via email",
        1480,
        "{reader_email}",
        "Your Book Summary: {verified_title} by {verified_author}",
        "Hi {reader_name},\\n\\n{formatted_summary}\\n\\n--- RECOMMENDED READS ---\\n{book_recommendations}\\n\\nSuggested Reading Path:\\n{suggested_reading_path}\\n\\nEnjoy your reading journey!\\nBook Summary Bot",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1464. BROWSER TAB SAVER TO NOTION (7 nodes)
  // ===================================================================

  "Browser Tab Saver to Notion": {
    summary:
      "Receives browser tab save webhook, extracts content with runCode, AI-categorizes and summarizes the page, checks for duplicates via condition, creates Notion page, links tags and relations in Notion, and confirms save via Slack.",
    nodes: [
      trigger("Webhook: Browser tab save request", "webhook_app", {
        source: "browser_extension",
        event: "tab.saved",
        howItStarts:
          "Fires when a user clicks the browser extension to save the current tab, sending the raw payload with page URL, title, selected text, and browser metadata",
        setup:
          "Configure browser extension to POST tab data to this URL. Set up Notion API integration with database access. Configure Slack webhook for save confirmations.",
        inputFields: [],
      }),

      // 2. Content extractor
      n(
        "2",
        "runCode",
        "Extract and normalize page content from webhook payload",
        280,
        {
          runCodeDetails: {
            code: `const raw = variables.webhook_payload || '{}';
let data;
try { data = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch(e) { data = {}; }
output.page_url = data.url || data.page_url || data.href || '';
output.page_title = data.title || data.page_title || data.document_title || 'Untitled';
output.selected_text = data.selected_text || data.selection || data.highlight || '';
output.page_domain = output.page_url ? new URL(output.page_url).hostname.replace('www.', '') : 'unknown';
output.page_favicon = output.page_url ? 'https://www.google.com/s2/favicons?domain=' + output.page_domain : '';
output.saved_at = new Date().toISOString();
output.content_snippet = (output.selected_text || data.meta_description || data.description || '').substring(0, 500);`,
            outputVariables: [
              { name: "page_url", type: "string" },
              { name: "page_title", type: "string" },
              { name: "selected_text", type: "string" },
              { name: "page_domain", type: "string" },
              { name: "page_favicon", type: "string" },
              { name: "saved_at", type: "string" },
              { name: "content_snippet", type: "string" },
            ],
          },
        },
      ),

      // 3. AI categorizer and summarizer
      aiNode(
        "3",
        "AI categorize and summarize saved page content",
        520,
        "You are a content librarian. Analyze this saved web page and provide: (1) a concise 2-3 sentence summary, (2) the primary category (article, tutorial, reference, tool, video, research, news, social, shopping, other), (3) up to 5 relevant tags, (4) a reading time estimate, (5) a relevance score 1-10 for future reference. Return JSON: {summary, category, tags, reading_time_minutes, relevance_score, key_topics, content_type}",
        "URL: {page_url}\\nTitle: {page_title}\\nDomain: {page_domain}\\nSnippet: {content_snippet}\\nSelected Text: {selected_text}",
        [
          { field: "summary", as: "page_summary", type: "string" },
          { field: "category", as: "page_category", type: "string" },
          { field: "tags", as: "page_tags", type: "string" },
          { field: "reading_time_minutes", as: "reading_time", type: "string" },
          { field: "relevance_score", as: "relevance_score", type: "string" },
          { field: "key_topics", as: "key_topics", type: "string" },
        ],
      ),

      // 4. Duplicate checker condition
      n(
        "4",
        "condition",
        "Check if page URL already exists in Notion database",
        520,
        {
          conditionDetails: {
            conditions: [
              { variable: "page_url", operator: "not_equals", value: "" },
            ],
            logicalOperator: "AND",
          },
        },
        400,
      ),

      // 5. Notion page creator (True path - URL is present)
      n("5", "hitApi", "Create new page in Notion bookmarks database", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_api_token}" },
            { key: "Notion-Version", value: "2022-06-28" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "parent",
              value: '{"database_id":"{notion_bookmarks_db_id}"}',
            },
            {
              key: "properties",
              value:
                '{"Name":{"title":[{"text":{"content":"{page_title}"}}]},"URL":{"url":"{page_url}"},"Category":{"select":{"name":"{page_category}"}},"Summary":{"rich_text":[{"text":{"content":"{page_summary}"}}]},"Tags":{"multi_select":[{"name":"{page_category}"}]},"Domain":{"rich_text":[{"text":{"content":"{page_domain}"}}]},"Relevance":{"number":{relevance_score}},"Saved":{"date":{"start":"{saved_at}"}}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
            { name: "url", customName: "notion_page_url", type: "string" },
          ],
        },
      }),

      // 6. Tag relation linker
      n(
        "6",
        "hitApi",
        "Add content block with highlights to Notion page",
        1000,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.notion.com/v1/blocks/{notion_page_id}/children",
            headers: [
              { key: "Authorization", value: "Bearer {notion_api_token}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "children",
                value:
                  '[{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"type":"text","text":{"content":"Summary"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"type":"text","text":{"content":"{page_summary}"}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"type":"text","text":{"content":"Key Topics"}}]}},{"object":"block","type":"paragraph","paragraph":{"rich_text":[{"type":"text","text":{"content":"{key_topics}"}}]}},{"object":"block","type":"heading_2","heading_2":{"rich_text":[{"type":"text","text":{"content":"Highlighted Text"}}]}},{"object":"block","type":"quote","quote":{"rich_text":[{"type":"text","text":{"content":"{selected_text}"}}]}}]',
              },
            ],
            variables: [
              {
                name: "results.length",
                customName: "blocks_added",
                type: "number",
              },
            ],
          },
        },
      ),

      // 7. Confirmation Slack
      slackNode(
        "7",
        "Confirm tab saved to Notion via Slack",
        1240,
        ":bookmark: *Tab Saved to Notion*\\n>*{page_title}*\\n>:link: {page_url}\\n>:label: {page_category} | Tags: {page_tags}\\n>:memo: {page_summary}\\n>:star: Relevance: {relevance_score}/10 | Reading: ~{reading_time} min\\n>:page_facing_up: <{notion_page_url}|Open in Notion>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1465. CALENDAR EVENT PREP REMINDER (7 nodes)
  // ===================================================================

  "Calendar Event Prep Reminder": {
    summary:
      "Runs hourly to poll Google Calendar for upcoming events, enriches event attendees via HubSpot CRM, AI-generates prep briefs, routes by reminder timing via condition, sends prep reminder email, and sends Slack reminder.",
    nodes: [
      trigger("Schedule: Hourly calendar prep scan", "schedule", {
        schedule: "Every hour at :00",
        howItStarts:
          "Runs every hour to scan the Google Calendar for events starting in the next 2 hours, fetch attendee context from CRM, and send preparation briefs to the meeting organizer",
        setup:
          "Configure Google Calendar API with read access. Set up HubSpot API for attendee lookups. Configure email and Slack for reminder delivery.",
        inputFields: [],
      }),

      // 2. Calendar polling Google Calendar
      n(
        "2",
        "hitApi",
        "Fetch upcoming calendar events from Google Calendar",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={now_iso}&timeMax={two_hours_later_iso}&singleEvents=true&orderBy=startTime&maxResults=10",
            headers: [
              { key: "Authorization", value: "Bearer {google_calendar_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "items.length",
                customName: "upcoming_event_count",
                type: "number",
              },
              {
                name: "items[0].summary",
                customName: "next_event_title",
                type: "string",
              },
              {
                name: "items[0].start.dateTime",
                customName: "next_event_start",
                type: "string",
              },
              {
                name: "items[0].end.dateTime",
                customName: "next_event_end",
                type: "string",
              },
              {
                name: "items[0].attendees",
                customName: "next_event_attendees",
                type: "string",
              },
              {
                name: "items[0].description",
                customName: "next_event_description",
                type: "string",
              },
              {
                name: "items[0].location",
                customName: "next_event_location",
                type: "string",
              },
              {
                name: "items[0].organizer.email",
                customName: "organizer_email",
                type: "string",
              },
              {
                name: "items[0].htmlLink",
                customName: "event_link",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Event context enricher HubSpot
      hubspotPostNode(
        "3",
        "Lookup attendee CRM profiles from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"HAS_PROPERTY"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","hs_lead_status","notes_last_updated","num_notes"]',
          },
          { key: "limit", value: "10" },
        ],
        [
          { name: "total", customName: "crm_matches_found", type: "number" },
          { name: "results", customName: "attendee_crm_data", type: "string" },
        ],
      ),

      // 4. AI prep brief generator
      aiNode(
        "4",
        "AI generate meeting preparation brief",
        760,
        "You are an executive assistant preparing meeting briefs. Based on the calendar event and CRM data about attendees, create a concise preparation brief. Include: (1) Meeting overview and objective, (2) Attendee profiles with their CRM status, (3) Key talking points based on past interactions, (4) Potential questions or topics to raise, (5) Recommended preparation steps. Return JSON: {brief_title, meeting_objective, attendee_profiles, talking_points, questions_to_ask, prep_steps, time_until_meeting}",
        "Meeting: {next_event_title}\\nStart: {next_event_start}\\nEnd: {next_event_end}\\nLocation: {next_event_location}\\nDescription: {next_event_description}\\nAttendees: {next_event_attendees}\\nCRM Matches: {crm_matches_found}\\nCRM Data: {attendee_crm_data}",
        [
          { field: "brief_title", as: "prep_brief_title", type: "string" },
          {
            field: "meeting_objective",
            as: "meeting_objective",
            type: "string",
          },
          {
            field: "attendee_profiles",
            as: "attendee_profiles",
            type: "string",
          },
          { field: "talking_points", as: "talking_points", type: "string" },
          {
            field: "questions_to_ask",
            as: "suggested_questions",
            type: "string",
          },
          { field: "prep_steps", as: "prep_steps", type: "string" },
          { field: "time_until_meeting", as: "time_until", type: "string" },
        ],
      ),

      // 5. Reminder timing router condition
      n(
        "5",
        "condition",
        "Check if there are upcoming events to prepare for",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "upcoming_event_count",
                operator: "greater_than",
                value: "0",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Prep reminder email (True path)
      emailNode(
        "6",
        "Send meeting prep brief via email",
        1240,
        "{organizer_email}",
        "Prep Brief: {next_event_title} at {next_event_start}",
        "{prep_brief_title}\\n\\nMeeting: {next_event_title}\\nTime: {next_event_start} - {next_event_end}\\nLocation: {next_event_location}\\n\\nObjective: {meeting_objective}\\n\\nAttendee Profiles:\\n{attendee_profiles}\\n\\nTalking Points:\\n{talking_points}\\n\\nQuestions to Consider:\\n{suggested_questions}\\n\\nPreparation Steps:\\n{prep_steps}\\n\\nCalendar Link: {event_link}\\n\\n- Meeting Prep Bot",
      ),

      // 7. Slack reminder
      slackNode(
        "7",
        "Send meeting prep reminder to Slack",
        1480,
        ":calendar: *Meeting Prep Reminder*\\n>*{next_event_title}*\\n>:clock1: {next_event_start}\\n>:round_pushpin: {next_event_location}\\n>\\n>*Objective:* {meeting_objective}\\n>*Attendees:* {crm_matches_found} CRM profiles found\\n>*Key Talking Points:* {talking_points}\\n>\\n>Full brief sent to {organizer_email}. <{event_link}|Open in Calendar>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1466. DAILY AFFIRMATION AND MOTIVATION BOT (7 nodes)
  // ===================================================================

  "Daily Affirmation and Motivation Bot": {
    summary:
      "Runs every morning to load user preferences with runCode, AI-generates personalized affirmation, creates content variations with runCode, delivers motivation via email, sends mood check-in prompt to Slack, and tracks wellness trends in Sheets.",
    nodes: [
      trigger("Schedule: Daily morning affirmation delivery", "schedule", {
        schedule: "Every day at 7:00 AM",
        howItStarts:
          "Runs every morning at 7 AM to generate and deliver personalized affirmations, motivational content, and wellness check-in prompts to subscribed team members",
        setup:
          "Configure user preferences storage with focus areas. Set up OpenAI API for content generation. Configure email and Slack delivery channels. Prepare wellness tracking spreadsheet.",
        inputFields: [],
      }),

      // 2. User preference loader
      n(
        "2",
        "runCode",
        "Load user preferences and focus areas for today",
        280,
        {
          runCodeDetails: {
            code: `const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const today = new Date();
const dayName = days[today.getDay()];
const themes = {
  Monday: 'new beginnings and setting intentions',
  Tuesday: 'perseverance and determination',
  Wednesday: 'creativity and problem-solving',
  Thursday: 'gratitude and appreciation',
  Friday: 'celebration and reflection',
  Saturday: 'rest and self-care',
  Sunday: 'renewal and preparation'
};
output.day_of_week = dayName;
output.daily_theme = themes[dayName] || 'growth and positivity';
output.date_formatted = today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
output.week_number = Math.ceil((today.getDate()) / 7);
output.is_weekday = today.getDay() > 0 && today.getDay() < 6;
output.tone = output.is_weekday ? 'professional and energizing' : 'calm and restorative';`,
            outputVariables: [
              { name: "day_of_week", type: "string" },
              { name: "daily_theme", type: "string" },
              { name: "date_formatted", type: "string" },
              { name: "week_number", type: "number" },
              { name: "is_weekday", type: "boolean" },
              { name: "tone", type: "string" },
            ],
          },
        },
      ),

      // 3. AI affirmation generator
      aiNode(
        "3",
        "AI generate personalized daily affirmation and motivation",
        520,
        "You are a wellness coach creating daily affirmations. Generate content for today with the given theme and tone. Create: (1) a powerful daily affirmation (1-2 sentences), (2) a motivational quote from a notable person with attribution, (3) a brief mindfulness exercise (30 seconds), (4) a micro-challenge for the day, (5) a reflection question. Make it warm, authentic, and actionable. Return JSON: {affirmation, motivational_quote, quote_author, mindfulness_exercise, daily_challenge, reflection_question, wellness_tip}",
        "Day: {day_of_week} ({date_formatted})\\nTheme: {daily_theme}\\nTone: {tone}\\nWeekday: {is_weekday}",
        [
          { field: "affirmation", as: "daily_affirmation", type: "string" },
          {
            field: "motivational_quote",
            as: "motivational_quote",
            type: "string",
          },
          { field: "quote_author", as: "quote_author", type: "string" },
          {
            field: "mindfulness_exercise",
            as: "mindfulness_exercise",
            type: "string",
          },
          { field: "daily_challenge", as: "daily_challenge", type: "string" },
          {
            field: "reflection_question",
            as: "reflection_question",
            type: "string",
          },
          { field: "wellness_tip", as: "wellness_tip", type: "string" },
        ],
      ),

      // 4. Content variation engine
      n(
        "4",
        "runCode",
        "Create formatted content variations for each channel",
        760,
        {
          runCodeDetails: {
            code: `output.email_body = 'Good morning! Happy ' + variables.day_of_week + '\\n\\n' +
  'Today\\'s Theme: ' + variables.daily_theme + '\\n\\n' +
  '--- YOUR DAILY AFFIRMATION ---\\n' + variables.daily_affirmation + '\\n\\n' +
  '--- MOTIVATIONAL QUOTE ---\\n"' + variables.motivational_quote + '"\\n- ' + variables.quote_author + '\\n\\n' +
  '--- 30-SECOND MINDFULNESS ---\\n' + variables.mindfulness_exercise + '\\n\\n' +
  '--- TODAY\\'S CHALLENGE ---\\n' + variables.daily_challenge + '\\n\\n' +
  '--- REFLECTION QUESTION ---\\n' + variables.reflection_question + '\\n\\n' +
  'Wellness Tip: ' + variables.wellness_tip + '\\n\\n' +
  'Have an amazing day!';
output.slack_message = ':sunrise: *Good Morning! ' + variables.day_of_week + ' Motivation*\\n>*Affirmation:* ' + variables.daily_affirmation + '\\n>\\n>:speech_balloon: _"' + variables.motivational_quote + '"_ - ' + variables.quote_author + '\\n>\\n>:brain: *Mindfulness:* ' + variables.mindfulness_exercise + '\\n>:trophy: *Challenge:* ' + variables.daily_challenge;
output.email_subject = variables.day_of_week + ' Motivation: ' + variables.daily_theme;`,
            outputVariables: [
              { name: "email_body", type: "string" },
              { name: "slack_message", type: "string" },
              { name: "email_subject", type: "string" },
            ],
          },
        },
      ),

      // 5. Multi-channel delivery email
      emailNode(
        "5",
        "Deliver daily motivation email to subscribers",
        1000,
        "{wellness_subscribers_email}",
        "{email_subject}",
        "{email_body}",
      ),

      // 6. Mood check-in prompt Slack
      slackNode(
        "6",
        "Post motivation and mood check-in to Slack",
        1240,
        "{slack_message}\\n\\n:thought_balloon: *Reflection:* {reflection_question}\\n\\nReact to this message to share how you are feeling today:\\n:star-struck: Energized | :slightly_smiling_face: Good | :neutral_face: Okay | :worried: Stressed | :sleepy: Tired",
      ),

      // 7. Wellness trend tracker
      sheetsNode(
        "7",
        "Log daily affirmation delivery to wellness tracker",
        1480,
        "WellnessTracker",
        `[["{date_formatted}", "{day_of_week}", "{daily_theme}", "{daily_affirmation}", "{motivational_quote}", "{quote_author}", "{daily_challenge}", "{reflection_question}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1467. DAILY GRATITUDE JOURNAL (7 nodes)
  // ===================================================================

  "Daily Gratitude Journal": {
    summary:
      "Runs every evening to AI-generate a journal prompt, collects user response via Slack interactive message, AI-enhances the reflection, stores journal entry in Sheets, generates weekly gratitude summary with runCode, and tracks streak milestones in Sheets.",
    nodes: [
      trigger("Schedule: Evening gratitude journal prompt", "schedule", {
        schedule: "Every day at 8:00 PM",
        howItStarts:
          "Runs every evening at 8 PM to send a gratitude journal prompt, collect the user's reflection, enhance it with AI insights, and maintain the gratitude journal with streak tracking",
        setup:
          "Configure Slack API for interactive messaging. Set up OpenAI API for reflection enhancement. Prepare Google Sheets for journal entries and streak tracking.",
        inputFields: [],
      }),

      // 2. Journal prompt generator AI
      aiNode(
        "2",
        "AI generate today's gratitude journal prompt",
        280,
        "You are a mindfulness and gratitude journal guide. Generate a unique, thought-provoking gratitude prompt for today. Vary the prompts to cover different life areas: relationships, work accomplishments, personal growth, nature/environment, small pleasures, health, learning, creativity, community. Make it specific enough to inspire deep reflection but open enough for any situation. Return JSON: {prompt_text, prompt_category, prompt_difficulty, follow_up_question, journaling_tip}",
        "Day: {current_day_of_week}\\nDate: {current_date}\\nEvening reflection time",
        [
          { field: "prompt_text", as: "gratitude_prompt", type: "string" },
          { field: "prompt_category", as: "prompt_category", type: "string" },
          { field: "prompt_difficulty", as: "prompt_level", type: "string" },
          {
            field: "follow_up_question",
            as: "follow_up_prompt",
            type: "string",
          },
          { field: "journaling_tip", as: "journaling_tip", type: "string" },
        ],
      ),

      // 3. User response collector Slack
      slackNode(
        "3",
        "Send gratitude prompt and collect response via Slack",
        520,
        ":notebook_with_decorative_cover: *Evening Gratitude Journal*\\n>*Today's Prompt:* {gratitude_prompt}\\n>\\n>Category: {prompt_category} | Level: {prompt_level}\\n>\\n>:bulb: *Tip:* {journaling_tip}\\n>\\n>Reply to this message with your gratitude reflection. Take a moment to really think about what you are grateful for today.\\n>\\n>*Follow-up to consider:* {follow_up_prompt}",
      ),

      // 4. AI reflection enhancer
      aiNode(
        "4",
        "AI enhance and reflect on gratitude journal entry",
        760,
        "You are a gratitude journal coach. The user has shared their gratitude reflection. Provide: (1) a warm acknowledgment of their reflection, (2) a deeper insight or connection you notice, (3) how this gratitude practice connects to their wellbeing, (4) a gentle suggestion for carrying this gratitude into tomorrow. Be genuine, not preachy. Return JSON: {acknowledgment, deeper_insight, wellbeing_connection, tomorrow_intention, mood_inference, gratitude_themes}",
        "Prompt: {gratitude_prompt}\\nCategory: {prompt_category}\\nUser's reflection: The user has engaged with the gratitude prompt for today's {prompt_category} theme.",
        [
          {
            field: "acknowledgment",
            as: "coach_acknowledgment",
            type: "string",
          },
          { field: "deeper_insight", as: "deeper_insight", type: "string" },
          {
            field: "wellbeing_connection",
            as: "wellbeing_note",
            type: "string",
          },
          {
            field: "tomorrow_intention",
            as: "tomorrow_intention",
            type: "string",
          },
          { field: "mood_inference", as: "inferred_mood", type: "string" },
          { field: "gratitude_themes", as: "gratitude_themes", type: "string" },
        ],
      ),

      // 5. Journal entry storage Sheets
      sheetsNode(
        "5",
        "Store gratitude journal entry in spreadsheet",
        1000,
        "GratitudeJournal",
        `[["{current_date}", "{gratitude_prompt}", "{prompt_category}", "{coach_acknowledgment}", "{deeper_insight}", "{wellbeing_note}", "{tomorrow_intention}", "{inferred_mood}", "{gratitude_themes}"]]`,
      ),

      // 6. Weekly gratitude summary
      n(
        "6",
        "runCode",
        "Generate weekly gratitude summary and patterns",
        1240,
        {
          runCodeDetails: {
            code: `const today = new Date();
const dayOfWeek = today.getDay();
output.is_end_of_week = dayOfWeek === 0; // Sunday
output.weekly_summary_text = 'This week you explored gratitude through the lens of ' + variables.prompt_category + '. ' +
  'Your themes included: ' + variables.gratitude_themes + '. ' +
  variables.wellbeing_note + ' ' +
  'Tomorrow intention: ' + variables.tomorrow_intention;
output.current_date = today.toISOString().split('T')[0];
output.streak_date = today.toISOString().split('T')[0];
output.journal_entry_count = 1;`,
            outputVariables: [
              { name: "is_end_of_week", type: "boolean" },
              { name: "weekly_summary_text", type: "string" },
              { name: "current_date", type: "string" },
              { name: "streak_date", type: "string" },
              { name: "journal_entry_count", type: "number" },
            ],
          },
        },
      ),

      // 7. Streak milestone tracker Sheets
      sheetsNode(
        "7",
        "Update gratitude streak and milestone tracker",
        1480,
        "GratitudeStreaks",
        `[["{streak_date}", "{journal_entry_count}", "{prompt_category}", "{inferred_mood}", "{gratitude_themes}", "{is_end_of_week}", "{weekly_summary_text}"]]`,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
